import React, { useMemo, useRef, useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as d3 from 'd3';
import { sankey as d3Sankey, sankeyLinkHorizontal } from 'd3-sankey';
import { 
  Users, Brain, Target, Zap, Gem, Crosshair, 
  Megaphone, Activity, MapPin, GraduationCap, 
  Briefcase, Filter, ChevronDown, ChevronUp, AlertTriangle, 
  BarChart3, PieChart, Star,
  GitPullRequest, Eye, Anchor, Scale,
  Calendar, ArrowRight, User, MousePointer2, Layers, Lightbulb,
  Thermometer, TrendingUp, AlertCircle, MessageSquare, ScatterChart,
  Radio, Tv, Smartphone, FileText, Check, X, TrendingDown,
  Vote, Split, Shield, ShoppingBag, Hash, Lock, Gauge
} from 'lucide-react';

// ====================================================================================
// 1. DATA IMPORTS
// ====================================================================================
import cohort1824 from '../data/demographics/cohort_summary_18_24.json';
import cohort2534 from '../data/demographics/cohort_summary_25_34.json';
import cohort3544 from '../data/demographics/cohort_summary_35_44.json';
import cohort4554 from '../data/demographics/cohort_summary_45_54.json';
import cohort5564 from '../data/demographics/cohort_summary_55_64.json';
import cohort6475 from '../data/demographics/cohort_summary_64_75.json';
import cohort75plus from '../data/demographics/cohort_summary_75_plus.json';

const COHORT_LOOKUP = {
  '18-24': cohort1824,
  '25-34': cohort2534,
  '35-44': cohort3544,
  '45-54': cohort4554,
  '55-64': cohort5564,
  '64-75': cohort6475,
  '75+':   cohort75plus
};

// ====================================================================================
// 2. CONSTANTS & CONFIG
// ====================================================================================

const AGE_BUCKETS = ['18-24', '25-34', '35-44', '45-54', '55-64', '64-75', '75+'];

const PARTY_COLORS = {
  "Labour": "#E4003B",
  "Conservative": "#0087DC",
  "Liberal Democrats": "#FAA61A",
  "Green": "#6AB023",
  "Reform UK": "#12B6CF",
  "Other/Abstain": "#64748b"
};

const PARTY_CONFIG = {
  labour: { name: 'Labour', color: 'text-red-500', bg: 'bg-red-500', border: 'border-red-500', hex: '#E4003B' },
  conservative: { name: 'Conservative', color: 'text-blue-500', bg: 'bg-blue-500', border: 'border-blue-500', hex: '#0087DC' },
  libdem: { name: 'Lib Dem', color: 'text-amber-500', bg: 'bg-amber-500', border: 'border-amber-500', hex: '#FAA61A' },
  reform: { name: 'Reform', color: 'text-cyan-400', bg: 'bg-cyan-400', border: 'border-cyan-400', hex: '#12B6CF' },
  green: { name: 'Green', color: 'text-green-500', bg: 'bg-green-500', border: 'border-green-500', hex: '#6AB023' },
  neutral: { name: 'General Electorate', color: 'text-slate-400', bg: 'bg-slate-500', border: 'border-slate-500', hex: '#64748b' }
};

const getPartyColor = (key) => {
    if (!key) return 'bg-slate-600';
    const k = key.toLowerCase().replace(' ', '').replace('_uk', '');
    if(k.includes('lib') && k.includes('dem')) return PARTY_CONFIG.libdem.bg;
    if(k.includes('liberal')) return PARTY_CONFIG.libdem.bg;
    if(k.includes('lab')) return PARTY_CONFIG.labour.bg;
    if(k.includes('con')) return PARTY_CONFIG.conservative.bg;
    if(k.includes('ref')) return PARTY_CONFIG.reform.bg;
    if(k.includes('gre')) return PARTY_CONFIG.green.bg;
    return 'bg-slate-600';
};

const getPartyHex = (key) => {
    if (!key) return '#64748b';
    const k = key.toLowerCase().replace(' ', '').replace('_uk', '');
    if(k.includes('lib') && k.includes('dem')) return PARTY_CONFIG.libdem.hex;
    if(k.includes('liberal')) return PARTY_CONFIG.libdem.hex;
    if(k.includes('lab')) return PARTY_CONFIG.labour.hex;
    if(k.includes('con')) return PARTY_CONFIG.conservative.hex;
    if(k.includes('ref')) return PARTY_CONFIG.reform.hex;
    if(k.includes('gre')) return PARTY_CONFIG.green.hex;
    return '#64748b';
};

const normalizePartyKey = (key) => {
    if (!key) return "Other/Abstain";
    const k = key.toLowerCase().replace(/_/g, ' ').replace('uk', '').trim();
    if (k.includes('lab')) return "Labour";
    if (k.includes('con')) return "Conservative";
    if (k.includes('lib') || k.includes('dem')) return "Liberal Democrats";
    if (k.includes('green')) return "Green";
    if (k.includes('ref')) return "Reform UK";
    return "Other/Abstain";
};

// ====================================================================================
// 3. CORE UI COMPONENTS
// ====================================================================================

const Panel = ({ title, icon: Icon, children, className = "", action = null }) => (
  <motion.div
    initial={{ opacity: 0, y: 10 }}
    animate={{ opacity: 1, y: 0 }}
    className={`bg-slate-900/40 backdrop-blur-md border border-slate-700/50 rounded-2xl overflow-hidden shadow-xl ${className}`}
  >
    {title && (
      <div className="flex items-center justify-between px-6 py-4 border-b border-white/5 bg-white/[0.02]">
        <h3 className="flex items-center text-sm font-bold text-slate-200 tracking-wide">
          {Icon && <Icon size={16} className="mr-2 text-slate-400" />}
          {title}
        </h3>
        {action}
      </div>
    )}
    <div className="p-6">{children}</div>
  </motion.div>
);

const ValueBadge = ({ value, trend = 'neutral' }) => {
  const colors = {
    positive: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
    negative: 'bg-rose-500/10 text-rose-400 border-rose-500/20',
    neutral: 'bg-slate-500/10 text-slate-400 border-slate-500/20',
    high: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
    critical: 'bg-red-500/10 text-red-400 border-red-500/20'
  };
  
  let colorKey = trend;
  if (typeof value === 'string') {
      if (value.includes('EXTREME') || value.includes('HIGH') || value.includes('Very High')) colorKey = 'critical';
      else if (value.includes('MEDIUM') || value.includes('Medium')) colorKey = 'high';
      else if (value.includes('LOW') || value.includes('Low')) colorKey = 'neutral';
  }

  return (
    <div className={`inline-flex items-center space-x-1 px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border ${colors[colorKey] || colors.neutral}`}>
      <span>{value}</span>
    </div>
  );
};

const InfoItem = ({ label, children, className = "" }) => (
    <div className={`py-3 border-b border-slate-700/50 last:border-b-0 ${className}`}>
      <dt className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-1">
        {label}
      </dt>
      <dd className="text-sm text-slate-200 leading-relaxed font-medium">
        {children}
      </dd>
    </div>
);

// ====================================================================================
// 4. DATA HYDRATION & LOGIC
// ====================================================================================

const hydrateData = (summaryData) => {
  if (!summaryData) return { totalPop: 0, variants: [], genderAgeSummary: {} };
  
  let variants = summaryData.variant_breakdown || [];
  const cohortSummary = summaryData.cohort_summary || [];

  const masterProfileMap = {};
  Object.values(COHORT_LOOKUP).forEach(cohortArr => {
      const cohort = Array.isArray(cohortArr) ? cohortArr[0] : cohortArr;
      if(cohort.profiles) {
          Object.values(cohort.profiles).forEach(p => {
              masterProfileMap[p.display_name] = p;
          });
      }
  });
  
  const needsPopHydration = variants.length === 0 || (variants.every(v => (v.total_population || 0) === 0) && cohortSummary.some(c => (c.estimated_population || 0) > 0));
  
  if (needsPopHydration) {
      const variantPopMap = {};
      if (cohortSummary.length > 0) {
          cohortSummary.forEach(cohort => {
              const cPop = cohort.estimated_population || 0;
              const vMap = cohort.variants || {};
              const totalWeight = Object.values(vMap).reduce((a,b) => a + Number(b), 0);
              if (totalWeight > 0) {
                  Object.entries(vMap).forEach(([vName, weight]) => {
                      variantPopMap[vName] = (Number(weight) / totalWeight) * cPop;
                  });
              }
          });
      } else {
         const totalCampaignPop = summaryData.summary?.campaign_total_voters || 100000;
         const cohortWeights = {
             '18-24': 0.11, '25-34': 0.14, '35-44': 0.13, '45-54': 0.14,
             '55-64': 0.12, '64-75': 0.10, '75+': 0.08
         };
         Object.entries(COHORT_LOOKUP).forEach(([ageKey, data]) => {
             const safeData = Array.isArray(data) ? data[0] : data;
             const profiles = Object.values(safeData.profiles || {});
             const cohortPop = totalCampaignPop * (cohortWeights[ageKey] || 0.1);
             const popPerProfile = profiles.length > 0 ? cohortPop / profiles.length : 0;
             profiles.forEach(p => {
                 variants.push({
                     name: p.display_name,
                     age_range: ageKey,
                     total_population: popPerProfile,
                     gender: p.display_name.toLowerCase().includes('female') ? 'Female' : 'Male'
                 });
             });
         });
      }

      if (Object.keys(variantPopMap).length > 0) {
        const nameCounts = {};
        variants.forEach(v => { nameCounts[v.name] = (nameCounts[v.name] || 0) + 1; });

        variants = variants.map(v => {
            const totalForName = variantPopMap[v.name] || 0;
            const count = nameCounts[v.name] || 1;
            const derivedPop = totalForName / count;
            return { ...v, total_population: derivedPop, estimated_population: derivedPop };
        });
      }
  }

  variants = variants.map(v => {
      const staticProfile = masterProfileMap[v.name] || Object.values(masterProfileMap).find(p => p.display_name.includes(v.name));
      let propensities = { labour: 0.2, conservative: 0.2, reform: 0.2, liberal_democrat: 0.2, green: 0.2 };
      if (staticProfile?.electoral_affinity_matrix) propensities = staticProfile.electoral_affinity_matrix;
      else if (v.political_profile?.party_propensity) propensities = v.political_profile.party_propensity;

      return {
          ...v,
          political_profile: { party_propensity: propensities },
          campaign_targeting: v.campaign_targeting || { persuadability_score: 0.5, activation_difficulty: 0.5 },
          // Ensure we have AIO data if available from static profile
          aio: staticProfile?.aio_media_consumption || {},
          static_profile_data: staticProfile // Attach full static profile for deep dives
      };
  });

  const totalPop = variants.reduce((acc, v) => acc + (v.total_population || 0), 0);
  
  const genderAgeSummary = {};
  AGE_BUCKETS.forEach(bucket => {
    genderAgeSummary[bucket] = { 
        total_population: 0, 
        male_pct: 0.5, 
        female_pct: 0.5, 
        male_count: 0, 
        female_count: 0,
        avg_turnout: 0,
        turnout_weighted_sum: 0,
        dominant_party: null,
        party_counts: {}
    };
  });

  variants.forEach(v => {
      let bucket = '75+';
      if (AGE_BUCKETS.includes(v.age_range)) bucket = v.age_range;
      else if (v.avg_age) {
        if (v.avg_age < 25) bucket = '18-24';
        else if (v.avg_age < 35) bucket = '25-34';
        else if (v.avg_age < 45) bucket = '35-44';
        else if (v.avg_age < 55) bucket = '45-54';
        else if (v.avg_age < 65) bucket = '55-64';
        else if (v.avg_age < 75) bucket = '64-75';
      }

      if (genderAgeSummary[bucket]) {
          const pop = v.total_population || 0;
          genderAgeSummary[bucket].total_population += pop;
          
          if (v.gender === 'Male') genderAgeSummary[bucket].male_count += pop;
          else if (v.gender === 'Female') genderAgeSummary[bucket].female_count += pop;
          else {
             genderAgeSummary[bucket].male_count += (pop/2);
             genderAgeSummary[bucket].female_count += (pop/2);
          }

          // Weighted Turnout
          const turnout = v.base_turnout || v.campaign_targeting?.turnout_probability || 0.5;
          genderAgeSummary[bucket].turnout_weighted_sum += (turnout * pop);

          // Party Affinity
          const props = v.political_profile?.party_propensity || {};
          Object.entries(props).forEach(([party, score]) => {
              const normParty = normalizePartyKey(party);
              if (!genderAgeSummary[bucket].party_counts[normParty]) genderAgeSummary[bucket].party_counts[normParty] = 0;
              genderAgeSummary[bucket].party_counts[normParty] += (score * pop);
          });
      }
  });

  Object.values(genderAgeSummary).forEach(s => {
      if (s.total_population > 0) {
          s.male_pct = s.male_count / s.total_population;
          s.female_pct = s.female_count / s.total_population;
          s.avg_turnout = s.turnout_weighted_sum / s.total_population;
          
          // Determine Dominant Party
          let maxScore = 0;
          let domParty = "Neutral";
          Object.entries(s.party_counts).forEach(([p, score]) => {
              if (score > maxScore) { maxScore = score; domParty = p; }
          });
          s.dominant_party = domParty;
      }
  });

  return { totalPop, variants, genderAgeSummary };
};

// --- HELPER: LOGIC TO CALIBRATE VARIANTS (THE HYDRAULIC PRESS v2) ---
const getCalibratedVariants = (rawVariants, predictedVoteShares, totalPopulation) => {
    if (!predictedVoteShares || !rawVariants.length) return rawVariants;

    const partyKeys = Object.keys(PARTY_COLORS);
    const calibrationFactors = {};
    const theoreticalTotals = {};
    
    partyKeys.forEach(p => theoreticalTotals[p] = 0);

    // 1. Calculate Theoretical Totals
    rawVariants.forEach(v => {
        const pop = v.total_population || 0;
        const prof = v.political_profile?.party_propensity || {};
        let totalProp = 0;

        Object.entries(prof).forEach(([pkey, pval]) => {
             const normKey = normalizePartyKey(pkey);
             if (theoreticalTotals[normKey] !== undefined) {
                 theoreticalTotals[normKey] += (pop * pval);
                 totalProp += pval;
             }
        });
        if (totalProp < 0.99) {
            theoreticalTotals["Other/Abstain"] += (pop * (1.0 - totalProp));
        }
    });

    // 2. Calculate Factors
    partyKeys.forEach(uiParty => {
        let modelPercent = 0;
        const dbKey = Object.keys(predictedVoteShares).find(k => normalizePartyKey(k) === uiParty);
        if (dbKey) modelPercent = predictedVoteShares[dbKey];
        else if (uiParty === 'Other/Abstain') modelPercent = predictedVoteShares.other || 0;

        const targetTotalVoters = (modelPercent / 100) * totalPopulation;
        const theoreticalTotalRaw = theoreticalTotals[uiParty];

        if (targetTotalVoters > 0 && theoreticalTotalRaw > 0) {
             calibrationFactors[uiParty] = targetTotalVoters / theoreticalTotalRaw;
        } else if (targetTotalVoters === 0) {
             calibrationFactors[uiParty] = 0; 
        } else {
             calibrationFactors[uiParty] = 1; 
        }
    });

    // 3. Apply Factors & Normalize
    return rawVariants.map(v => {
        const originalProps = v.political_profile?.party_propensity || {};
        let weightedProps = {};
        let totalWeight = 0;
        
        Object.entries(originalProps).forEach(([pkey, pval]) => {
            const normKey = normalizePartyKey(pkey);
            const factor = calibrationFactors[normKey] !== undefined ? calibrationFactors[normKey] : 1.0;
            const wVal = pval * factor;
            weightedProps[pkey] = wVal;
            totalWeight += wVal;
        });

        let currentTotal = Object.values(originalProps).reduce((a,b)=>a+b,0);
        if(currentTotal < 1) {
            const remainder = (1 - currentTotal);
            const otherFactor = calibrationFactors["Other/Abstain"] || 1.0;
            const weightedRemainder = remainder * otherFactor;
            totalWeight += weightedRemainder;
        }

        const normalizedProps = {};
        if (totalWeight > 0) {
            Object.entries(weightedProps).forEach(([pkey, wVal]) => {
                 normalizedProps[pkey] = wVal / totalWeight;
            });
        } else {
            normalizedProps = originalProps;
        }

        return {
            ...v,
            political_profile: {
                ...v.political_profile,
                party_propensity: normalizedProps
            }
        };
    });
};

// ====================================================================================
// 5. CHART COMPONENTS
// ====================================================================================

// --- GENDER POLARIZATION CHART ---
const GenderPolarizationChart = ({ data }) => {
  const parties = ['Labour', 'Conservative', 'Reform', 'Green', 'Liberal Democrats'];
  const polarizationData = parties.map(party => {
    const key = party.toLowerCase().replace(' ', '');
    let pKey = key;
    if (key === 'libdem' || key === 'liberaldemocrats') pKey = 'liberal_democrat';
    if (key === 'reform') pKey = 'reform_uk';

    let maleVal = 0, femaleVal = 0;
    let maleCount = 0, femaleCount = 0;

    data.forEach(v => {
       const prof = v.political_profile?.party_propensity || {};
       const prop = prof[pKey] || prof[key] || prof[party] || 0.00;
       
       if (v.gender === 'Male') { maleVal += prop; maleCount++; }
       if (v.gender === 'Female') { femaleVal += prop; femaleCount++; }
    });

    return { 
      party, 
      male: maleCount > 0 ? maleVal / maleCount : 0.00, 
      female: femaleCount > 0 ? femaleVal / femaleCount : 0.00 
    };
  }); 

  return (
    <div className="space-y-5">
      <div className="flex justify-between items-center mb-2">
        <h4 className="text-xs font-bold text-slate-500 uppercase tracking-widest">Schism Meter (Calibrated)</h4>
        <div className="flex gap-3 text-[10px] font-bold uppercase">
          <div className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-pink-500 shadow-[0_0_5px_#ec4899]"></span> F</div>
          <div className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-blue-500 shadow-[0_0_5px_#3b82f6]"></span> M</div>
        </div>
      </div>
      <div className="space-y-6">
        {polarizationData.map((d) => {
          const maleDisplay = Math.min(d.male, 1.0);
          const femaleDisplay = Math.min(d.female, 1.0);
          const min = Math.min(maleDisplay, femaleDisplay);
          const max = Math.max(maleDisplay, femaleDisplay);
          const width = Math.max((max - min) * 100, 1);
          const left = min * 100;
          const delta = Math.abs(maleDisplay - femaleDisplay);
          const isSchism = delta > 0.05; 
          
          return (
            <div key={d.party} className="relative h-8">
              <div className="absolute -top-3 left-0 w-full flex justify-between items-end">
                  <div className="text-[9px] font-bold text-slate-400 uppercase flex items-center gap-2">
                    <span className={`${delta > 0.1 ? 'text-white' : ''}`}>{d.party}</span>
                    {isSchism && <span className="text-[8px] text-rose-400 bg-rose-500/10 px-1.5 rounded border border-rose-500/20 tracking-wider">SCHISM ({(delta*100).toFixed(0)}%)</span>}
                  </div>
              </div>
              <div className="absolute top-1/2 left-0 w-full h-1 bg-slate-800 rounded-full mt-1"></div>
              <div 
                className={`absolute top-1/2 h-1 rounded-full mt-1 z-0 ${isSchism ? 'bg-gradient-to-r from-rose-500/50 via-purple-500/50 to-rose-500/50' : 'bg-slate-600'}`} 
                style={{ left: `${left}%`, width: `${width}%` }} 
              />
              <motion.div initial={{ left: '0%' }} animate={{ left: `${femaleDisplay * 100}%` }} className={`absolute top-1/2 mt-1 -translate-y-1/2 w-3 h-3 rounded-full border-2 border-slate-900 z-10 shadow-lg ${femaleDisplay > maleDisplay ? 'bg-pink-500 ring-2 ring-pink-500/30' : 'bg-pink-900/50 border-pink-500'}`} />
              <motion.div initial={{ left: '0%' }} animate={{ left: `${maleDisplay * 100}%` }} className={`absolute top-1/2 mt-1 -translate-y-1/2 w-3 h-3 rounded-full border-2 border-slate-900 z-10 shadow-lg ${maleDisplay > femaleDisplay ? 'bg-blue-500 ring-2 ring-blue-500/30' : 'bg-blue-900/50 border-blue-500'}`} />
            </div>
          )
        })}
      </div>
    </div>
  );
};

// --- REACTIVE ISSUE HEATMAP ---
const ReactiveIssueHeatmap = ({ profiles, variantData }) => {
    const issues = useMemo(() => {
        const aggregator = {};
        let totalWeight = 0;
        profiles.forEach(p => {
            const localVariant = variantData.find(v => v.name === p.display_name);
            const pop = localVariant ? (localVariant.total_population || 0) : 0;
            if (pop === 0) return;
            const concerns = p.key_concerns || [];
            concerns.forEach(str => {
                const match = str.match(/^(.*?)\s*\((\d*\.?\d+)\)$/);
                let issueName = str;
                let weight = 0.5;
                if (match) { issueName = match[1].trim(); weight = parseFloat(match[2]); } 
                else { issueName = str.replace(/\(.*\)/, '').trim(); }
                if (!aggregator[issueName]) aggregator[issueName] = 0;
                aggregator[issueName] += (pop * weight);
                totalWeight += (pop * weight);
            });
        });
        return Object.entries(aggregator)
            .map(([name, score]) => ({ name, score, pct: totalWeight > 0 ? score / totalWeight : 0 }))
            .sort((a, b) => b.score - a.score)
            .slice(0, 6);
    }, [profiles, variantData]);

    if (issues.length === 0) return <div className="text-xs text-slate-500 italic">No concern data for this cohort.</div>;

    return (
        <div className="space-y-3">
             <div className="flex items-center gap-2 mb-2">
                <Thermometer size={14} className="text-rose-400" />
                <h4 className="text-xs font-bold text-slate-400 uppercase tracking-widest">Weighted Issue Heatmap</h4>
            </div>
            {issues.map((issue, i) => (
                <div key={i} className="group">
                    <div className="flex justify-between items-end text-xs mb-1">
                        <span className="text-slate-200 font-medium group-hover:text-rose-300 transition-colors">{issue.name}</span>
                        <span className="text-slate-500 font-mono text-[10px]">{(issue.pct * 100).toFixed(0)}</span>
                    </div>
                    <div className="w-full bg-slate-800/50 rounded-full h-1.5 overflow-hidden">
                        <motion.div initial={{ width: 0 }} animate={{ width: `${issue.pct * 100 * 2.5}%` }} className="h-full bg-gradient-to-r from-rose-500/50 to-rose-500 rounded-full" />
                    </div>
                </div>
            ))}
            <div className="text-[9px] text-slate-500 text-right mt-1">*Weighted by local population prevalence</div>
        </div>
    );
};

// --- INTELLIGENCE DASHBOARD ---
const IntelligenceDashboard = ({ context, intelligence }) => {
    return (
        <div className="space-y-6">
            <div className="bg-slate-800/20 rounded-lg p-4 border border-indigo-500/20 relative overflow-hidden">
                <div className="absolute top-0 left-0 w-1 h-full bg-indigo-500" />
                <div className="flex items-center gap-2 mb-2">
                    <Brain size={16} className="text-indigo-400" />
                    <h4 className="text-xs font-bold text-indigo-400 uppercase tracking-widest">Psychographic Profile</h4>
                </div>
                <p className="text-xs md:text-sm text-slate-300 leading-relaxed italic">"{context}"</p>
                {intelligence && (
                     <div className="mt-3 pt-3 border-t border-indigo-500/10 text-xs text-slate-400">
                        <span className="font-bold text-indigo-300">Drivers: </span> {intelligence}
                     </div>
                )}
            </div>
        </div>
    );
};

// --- GLOBAL POLITICAL STAR CHART (REPLACES SUNBURST) ---
const GlobalStarChart = ({ variantData }) => {
    const svgRef = useRef(null);
    const canvasRef = useRef(null);
    const containerRef = useRef(null);
    const [tooltip, setTooltip] = useState(null);
    const [dimensions, setDimensions] = useState({ width: 0, height: 600 });

    const getDominantParty = (propensity) => {
        let max = 0;
        let party = 'Neutral';
        Object.entries(propensity).forEach(([p, val]) => {
            if (val > max) { max = val; party = normalizePartyKey(p); }
        });
        return party;
    };

    const getRandomPartyColor = (propensity) => {
        const rand = Math.random();
        let cumulative = 0;
        for (const [p, val] of Object.entries(propensity)) {
            cumulative += val;
            if (rand <= cumulative) return getPartyHex(normalizePartyKey(p));
        }
        return '#64748b'; 
    };

    // Calculate cluster centers and variance
    const clusterData = useMemo(() => {
        return variantData.map(v => {
            const props = v.political_profile?.party_propensity || {};
            
            // --- CALIBRATION: Reform (Right +1), Green (Left +1), Labour (Left 0.6), Con (Right 0.6) ---
            const leftWeight = 
                ((props.green || 0) * 1.0) + 
                ((props.labour || 0) * 0.6) + 
                ((props.liberal_democrat || props.liberal_democrats || 0) * 0.1);

            const rightWeight = 
                ((props.reform || props.reform_uk || 0) * 1.0) + 
                ((props.conservative || 0) * 0.6) +
                ((props.liberal_democrat || props.liberal_democrats || 0) * 0.1);
            
            const xVal = rightWeight - leftWeight; 
            const yVal = v.base_turnout || 0.5;
            
            return {
                id: v.name,
                age: v.age_range,
                cx: xVal,
                cy: yVal,
                population: v.total_population || 0,
                party: getDominantParty(props),
                gender: v.gender,
                props: props
            };
        });
    }, [variantData]);

    useEffect(() => {
        if (!containerRef.current) return;
        const resizeObserver = new ResizeObserver(entries => {
            if (!entries || !entries[0]) return;
            const { width } = entries[0].contentRect;
            setDimensions(prev => ({ ...prev, width }));
        });
        resizeObserver.observe(containerRef.current);
        return () => resizeObserver.disconnect();
    }, []);

    useEffect(() => {
        if (!dimensions.width || !clusterData.length) return;

        const margin = { top: 30, right: 30, bottom: 40, left: 50 };
        const width = dimensions.width - margin.left - margin.right;
        const height = dimensions.height - margin.top - margin.bottom;

        const xScale = d3.scaleLinear().domain([-1.1, 1.1]).range([0, width]);
        const yScale = d3.scaleLinear().domain([0, 1]).range([height, 0]);

        const randn_bm = () => {
            let u = 0, v = 0;
            while(u === 0) u = Math.random();
            while(v === 0) v = Math.random();
            return Math.sqrt( -2.0 * Math.log( u ) ) * Math.cos( 2.0 * Math.PI * v );
        };

        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        canvas.width = width;
        canvas.height = height;
        canvas.style.left = `${margin.left}px`;
        canvas.style.top = `${margin.top}px`;

        ctx.clearRect(0, 0, width, height);
        ctx.globalCompositeOperation = "lighter";

        // Logic: 1 particle = ~100 voters
        const VOTERS_PER_PARTICLE = 100;
        
        clusterData.forEach(cluster => {
            // Cap max particles per cluster for performance if needed, but aim for accuracy
            const particleCount = Math.min(Math.floor(cluster.population / VOTERS_PER_PARTICLE), 3000);
            
            for (let i = 0; i < particleCount; i++) {
                const colorHex = getRandomPartyColor(cluster.props);
                ctx.fillStyle = colorHex;

                const xNoise = randn_bm() * 0.08; 
                const yNoise = randn_bm() * 0.05;
                
                const px = xScale(cluster.cx + xNoise);
                const py = yScale(cluster.cy + yNoise);
                
                // Increased size for visibility per user request
                const size = Math.random() * 2.5 + 1; 
                ctx.globalAlpha = Math.random() * 0.6 + 0.3;
                ctx.beginPath();
                ctx.arc(px, py, size, 0, Math.PI * 2);
                ctx.fill();
            }
        });

        const svg = d3.select(svgRef.current);
        svg.selectAll("*").remove();
        const g = svg.append("g").attr("transform", `translate(${margin.left},${margin.top})`);

        g.append("g").attr("class", "grid").attr("transform", `translate(0,${height})`).call(d3.axisBottom(xScale).ticks(5).tickFormat("")).style("opacity", 0.1);
        g.append("g").attr("class", "grid").call(d3.axisLeft(yScale).ticks(5).tickFormat("")).style("opacity", 0.1);
        
        g.append("text").attr("x", width/2).attr("y", height + 25).attr("text-anchor", "middle").attr("fill", "#64748b").style("font-size", "9px").style("letter-spacing", "1px").text("IDEOLOGICAL SPECTRUM (LEFT ⟷ RIGHT)");
        g.append("text").attr("transform", "rotate(-90)").attr("y", -30).attr("x", -height/2).attr("text-anchor", "middle").attr("fill", "#64748b").style("font-size", "9px").style("letter-spacing", "1px").text("TURNOUT PROBABILITY (LUMINOSITY)");

        g.selectAll(".hotspot")
            .data(clusterData)
            .enter().append("circle")
            .attr("cx", d => xScale(d.cx))
            .attr("cy", d => yScale(d.cy))
            .attr("r", 25) 
            .attr("fill", "transparent")
            .attr("cursor", "pointer")
            .on("mouseenter", (e, d) => {
                const rect = containerRef.current.getBoundingClientRect();
                setTooltip({ x: e.clientX - rect.left, y: e.clientY - rect.top, data: d });
                g.append("circle").attr("class", "highlighter").attr("cx", xScale(d.cx)).attr("cy", yScale(d.cy)).attr("r", 30).attr("fill", "none").attr("stroke", "white").attr("stroke-width", 1).attr("stroke-dasharray", "4 2");
            })
            .on("mouseleave", () => {
                setTooltip(null);
                g.selectAll(".highlighter").remove();
            });

    }, [dimensions, clusterData]);

    const isFlippedX = tooltip && (tooltip.x > (dimensions.width - 200));
    const isFlippedY = tooltip && (tooltip.y > (dimensions.height - 150));

    return (
        <div className="relative w-full h-[600px] bg-slate-950 rounded-lg overflow-hidden border border-slate-800" ref={containerRef}>
            <canvas ref={canvasRef} className="absolute pointer-events-none" />
            <svg ref={svgRef} className="absolute top-0 left-0 w-full h-full" />
            
            <AnimatePresence>
                {tooltip && (
                    <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0 }}
                        className="absolute z-50 pointer-events-none bg-slate-900/95 border border-slate-600 p-3 rounded-lg shadow-xl text-xs w-48"
                        style={{ 
                            left: isFlippedX ? tooltip.x - 10 : tooltip.x + 10, 
                            top: isFlippedY ? tooltip.y - 10 : tooltip.y + 10,
                            x: isFlippedX ? "-100%" : "0%",
                            y: isFlippedY ? "-100%" : "0%"
                        }}
                    >
                        <div className="font-bold text-white border-b border-slate-700 pb-1 mb-1">{tooltip.data.id}</div>
                        <div className="text-[10px] text-slate-500 uppercase mb-2">{tooltip.data.age} Cohort</div>
                        <div className="grid grid-cols-2 gap-2 text-slate-400">
                            <span>Pop:</span> <span className="text-white text-right">{tooltip.data.population.toLocaleString()}</span>
                            <span>Turnout:</span> <span className="text-white text-right">{(tooltip.data.cy * 100).toFixed(0)}%</span>
                            <span>Dominant:</span> <span className="text-right" style={{color: getPartyHex(tooltip.data.party)}}>{tooltip.data.party}</span>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

// --- MEDIA ECOSYSTEM BUBBLE ---
const MediaEcosystemBubble = ({ variantData }) => {
    const bubbleRef = useRef(null);
    
    const data = useMemo(() => {
        const mediaCounts = {};
        variantData.forEach(v => {
            const pop = v.total_population || 0;
            // Robust check for media data across various schema locations
            const aio = v.aio || v.static_profile_data?.aio_media_consumption || {};
            const channels = v.static_profile_data?.media_ecosystem?.primary_channels || [];
            
            // Add AIO scores
            Object.entries(aio).forEach(([channel, score]) => {
                if (!mediaCounts[channel]) mediaCounts[channel] = 0;
                mediaCounts[channel] += (score * pop);
            });

            // Add direct channel hits if AIO missing
            if (Object.keys(aio).length === 0 && channels.length > 0) {
                channels.forEach(ch => {
                    if (!mediaCounts[ch]) mediaCounts[ch] = 0;
                    mediaCounts[ch] += pop;
                });
            }
        });

        // Normalize if empty to prevent D3 errors
        if (Object.keys(mediaCounts).length === 0) return [];

        return Object.entries(mediaCounts)
            .map(([name, val]) => ({ name: name.replace(/_/g, ' '), value: val }))
            .sort((a,b) => b.value - a.value)
            .slice(0, 15);
    }, [variantData]);

    useEffect(() => {
        if(!bubbleRef.current || !data.length) return;
        const width = bubbleRef.current.clientWidth;
        const height = 300;

        const svg = d3.select(bubbleRef.current);
        svg.selectAll("*").remove();

        const pack = d3.pack().size([width, height]).padding(3);
        const root = d3.hierarchy({ children: data }).sum(d => d.value);
        const nodes = pack(root).leaves();

        const color = d3.scaleOrdinal(d3.schemeTableau10);

        const g = svg.append("g");

        const bubbles = g.selectAll("g").data(nodes).enter().append("g")
            .attr("transform", d => `translate(${d.x},${d.y})`);

        bubbles.append("circle")
            .attr("r", d => d.r)
            .style("fill", (d, i) => color(i))
            .style("opacity", 0.7)
            .style("stroke", "#1e293b")
            .style("stroke-width", 1);

        bubbles.append("text")
            .attr("dy", "0.3em")
            .style("text-anchor", "middle")
            .style("font-size", d => `${Math.max(4, Math.min(d.r / 3, 10))}px`) // Adjust font size logic
            .style("fill", "white")
            .style("font-weight", "bold")
            .style("pointer-events", "none")
            .text(d => d.r > 10 ? d.data.name.split(' ')[0] : ''); 

    }, [data]);

    return (
        <div className="w-full" ref={bubbleRef}>
            {data.length === 0 && <div className="text-xs text-slate-500 italic p-4 text-center">No media data for this selection.</div>}
            <svg className="w-full h-[300px]"></svg>
        </div>
    );
};

// --- DEMOGRAPHIC SANKEY ---
const DemographicSankey = ({ variantData, predictedVoteShares, totalPopulation }) => {
  const svgRef = useRef(null);
  const containerRef = useRef(null);
  const [tooltip, setTooltip] = useState(null);
  const [selectedNodeIndex, setSelectedNodeIndex] = useState(null);
  const [containerDim, setContainerDim] = useState({ width: 800, height: 600 });

  const getRelativePosition = (event) => {
      if (!containerRef.current) return { x: 0, y: 0 };
      const [x, y] = d3.pointer(event, containerRef.current);
      return { x, y };
  };

  useEffect(() => {
    if (!variantData || variantData.length === 0 || !containerRef.current) return;

    setContainerDim({
        width: containerRef.current.clientWidth,
        height: containerRef.current.clientHeight
    });

    const nodes = [];
    const links = [];
    const nodeMap = new Map();

    AGE_BUCKETS.forEach(bucket => {
      nodes.push({ name: bucket, type: 'cohort', color: '#94a3b8' });
      nodeMap.set(`COHORT_${bucket}`, nodes.length - 1);
    });

    const partyKeys = Object.keys(PARTY_COLORS);
    partyKeys.forEach(party => {
      nodes.push({ name: party, type: 'party', color: PARTY_COLORS[party] });
      nodeMap.set(`PARTY_${party}`, nodes.length - 1);
    });

    const calibrationFactors = {};
    const theoreticalTotals = {};
    partyKeys.forEach(p => theoreticalTotals[p] = 0);
    const uniqueVariants = {};
    
    variantData.forEach(v => {
      const pop = v.total_population || v.estimated_population || 0;
      if (pop <= 0) return;
      let ageBucket = '75+';
      if (AGE_BUCKETS.includes(v.age_range)) ageBucket = v.age_range;
      else if (v.avg_age) { 
         if (v.avg_age < 25) ageBucket = '18-24';
         else if (v.avg_age < 35) ageBucket = '25-34';
         else if (v.avg_age < 45) ageBucket = '35-44';
         else if (v.avg_age < 55) ageBucket = '45-54';
         else if (v.avg_age < 65) ageBucket = '55-64';
         else ageBucket = '64-75';
      }
      const variantKey = `${v.name} (${ageBucket})`; 
      if (!uniqueVariants[variantKey]) {
        uniqueVariants[variantKey] = {
            name: v.name, cohort: ageBucket, pop: 0,
            propensities: { "Labour": 0, "Conservative": 0, "Liberal Democrats": 0, "Green": 0, "Reform UK": 0, "Other/Abstain": 0 }
        };
      }
      uniqueVariants[variantKey].pop += pop;
      const prof = v.political_profile?.party_propensity || {};
      let totalProp = 0;
      Object.entries(prof).forEach(([pkey, pval]) => {
          const normKey = normalizePartyKey(pkey);
          if (uniqueVariants[variantKey].propensities[normKey] !== undefined) {
             uniqueVariants[variantKey].propensities[normKey] += (pop * pval);
             theoreticalTotals[normKey] += (pop * pval);
             totalProp += pval;
          }
      });
      if (totalProp < 0.99) {
          uniqueVariants[variantKey].propensities["Other/Abstain"] += (pop * (1.0 - totalProp));
          theoreticalTotals["Other/Abstain"] += (pop * (1.0 - totalProp));
      }
    });

    if (predictedVoteShares) {
        partyKeys.forEach(uiParty => {
            let modelPercent = 0;
            const dbKey = Object.keys(predictedVoteShares).find(k => normalizePartyKey(k) === uiParty);
            if (dbKey) modelPercent = predictedVoteShares[dbKey];
            else if (uiParty === 'Other/Abstain') modelPercent = predictedVoteShares.other || 0;
            const targetTotalVoters = (modelPercent / 100) * totalPopulation;
            const theoreticalTotalRaw = theoreticalTotals[uiParty];
            if (targetTotalVoters > 0 && theoreticalTotalRaw > 0) {
                 calibrationFactors[uiParty] = targetTotalVoters / theoreticalTotalRaw;
            } else if (targetTotalVoters === 0) {
                 calibrationFactors[uiParty] = 0; 
            } else {
                 calibrationFactors[uiParty] = 1; 
            }
        });
    }

    Object.entries(uniqueVariants).forEach(([key, data]) => {
        nodes.push({ name: data.name, type: 'variant', color: '#cbd5e1' });
        const variantNodeIndex = nodes.length - 1;
        nodeMap.set(key, variantNodeIndex);
        const cohortIndex = nodeMap.get(`COHORT_${data.cohort}`);
        if (cohortIndex !== undefined) {
            links.push({
                source: cohortIndex, target: variantNodeIndex, value: data.pop,
                gradientId: `grad-c-${cohortIndex}-${variantNodeIndex}`
            });
        }
        Object.entries(data.propensities).forEach(([partyName, rawMass]) => {
             const partyIndex = nodeMap.get(`PARTY_${partyName}`);
             const factor = calibrationFactors[partyName] !== undefined ? calibrationFactors[partyName] : 1.0;
             const finalValue = rawMass * factor;
             if (partyIndex !== undefined && finalValue > 1) { 
                 links.push({
                     source: variantNodeIndex, target: partyIndex, value: finalValue,
                     gradientId: `grad-p-${variantNodeIndex}-${partyIndex}`
                 });
             }
        });
    });

    if (nodes.length === 0 || links.length === 0) return;
    const graphData = { nodes, links };
    const width = containerRef.current.clientWidth || 800;
    const height = 650; 
    
    d3.select(svgRef.current).selectAll("*").remove();
    const svg = d3.select(svgRef.current).attr("width", width).attr("height", height).style("overflow", "visible")
        .on("click", () => setSelectedNodeIndex(null));
    const sankeyGenerator = d3Sankey().nodeWidth(15).nodePadding(12).extent([[1, 1], [width - 1, height - 6]]);

    try {
        const { nodes: graphNodes, links: graphLinks } = sankeyGenerator(graphData);
        const defs = svg.append("defs");
        graphLinks.forEach((link, i) => {
            const gradient = defs.append("linearGradient").attr("id", link.gradientId || `grad-${i}`).attr("gradientUnits", "userSpaceOnUse")
                .attr("x1", link.source.x1).attr("x2", link.target.x0);
            if (link.source.type === 'cohort') {
                 gradient.append("stop").attr("offset", "0%").attr("stop-color", "#64748b");
                 gradient.append("stop").attr("offset", "100%").attr("stop-color", "#94a3b8");
            } else {
                 gradient.append("stop").attr("offset", "0%").attr("stop-color", "#94a3b8");
                 gradient.append("stop").attr("offset", "100%").attr("stop-color", link.target.color);
            }
        });
        svg.append("g").selectAll("path").data(graphLinks).join("path").attr("d", sankeyLinkHorizontal())
            .attr("stroke", d => `url(#${d.gradientId})`).attr("stroke-width", d => Math.max(1, d.width))
            .attr("fill", "none").attr("class", "cursor-pointer")
            .attr("opacity", d => {
                if (selectedNodeIndex === null) return 0.35;
                const isConnected = d.source.index === selectedNodeIndex || d.target.index === selectedNodeIndex;
                return isConnected ? 0.85 : 0.05;
            }).style("transition", "opacity 0.3s ease")
            .on("mouseenter", function(event, d) {
                const { x, y } = getRelativePosition(event);
                d3.select(this).attr("opacity", 0.9).attr("stroke-width", d.width + 1);
                setTooltip({ x, y, content: (<div className="space-y-1"><div className="font-bold text-slate-100 border-b border-slate-600 pb-1 mb-1">{d.source.name} → {d.target.name}</div><div className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-xs"><span className="text-slate-400">Predicted Flow:</span><span className="text-white font-mono">{Math.round(d.value).toLocaleString()} Votes</span><span className="text-slate-400">Share:</span><span className="text-slate-300 font-mono">{((d.value / d.source.value) * 100).toFixed(1)}% of Source</span></div></div>) });
            }).on("mousemove", (event) => { const { x, y } = getRelativePosition(event); setTooltip(prev => prev ? ({ ...prev, x, y }) : null); })
            .on("mouseleave", function(event, d) {
                const isConnected = selectedNodeIndex !== null && (d.source.index === selectedNodeIndex || d.target.index === selectedNodeIndex);
                const baseOpacity = selectedNodeIndex === null ? 0.35 : (isConnected ? 0.85 : 0.05);
                d3.select(this).attr("opacity", baseOpacity).attr("stroke-width", d.width);
                setTooltip(null);
            });
        const nodeGroup = svg.append("g").selectAll("rect").data(graphNodes).join("g");
        nodeGroup.append("rect").attr("x", d => d.x0).attr("y", d => d.y0).attr("height", d => Math.max(0, d.y1 - d.y0)).attr("width", d => d.x1 - d.x0)
            .attr("fill", d => d.color).attr("rx", 2).attr("class", "cursor-pointer")
            .attr("stroke", d => selectedNodeIndex === d.index ? "white" : "none").attr("stroke-width", 2)
            .attr("opacity", d => {
                if (selectedNodeIndex === null) return 1;
                const isSelected = d.index === selectedNodeIndex;
                const isNeighbor = d.sourceLinks?.some(l => l.target.index === selectedNodeIndex) || d.targetLinks?.some(l => l.source.index === selectedNodeIndex);
                return (isSelected || isNeighbor) ? 1 : 0.3;
            }).on("click", (event, d) => { event.stopPropagation(); setSelectedNodeIndex(current => current === d.index ? null : d.index); })
            .on("mouseenter", function(event, d) {
                const { x, y } = getRelativePosition(event);
                setTooltip({ x, y, content: (<div><div className="font-bold text-white mb-1" style={{ color: d.type === 'party' ? d.color : 'white' }}>{d.name}</div><div className="text-xs text-slate-300">Total: {Math.round(d.value).toLocaleString()} voters</div><div className="text-[10px] text-slate-500 uppercase mt-1">{d.type}</div></div>) });
            }).on("mousemove", (event) => { const { x, y } = getRelativePosition(event); setTooltip(prev => prev ? ({ ...prev, x, y }) : null); }).on("mouseleave", () => setTooltip(null));
        nodeGroup.append("text").attr("x", d => d.x0 < width / 2 ? d.x1 + 6 : d.x0 - 6).attr("y", d => (d.y1 + d.y0) / 2).attr("dy", "0.35em")
            .attr("text-anchor", d => d.x0 < width / 2 ? "start" : "end").text(d => (d.value > 0) ? d.name : "").style("fill", "#e2e8f0").style("font-size", "10px")
            .style("font-weight", "600").style("pointer-events", "none").style("text-shadow", "0 1px 2px rgba(0,0,0,0.8)")
            .style("opacity", d => {
                if (selectedNodeIndex === null) return d.value > 100 ? 1 : 0;
                const isSelected = d.index === selectedNodeIndex;
                const isNeighbor = d.sourceLinks?.some(l => l.target.index === selectedNodeIndex) || d.targetLinks?.some(l => l.source.index === selectedNodeIndex);
                return (isSelected || isNeighbor) ? 1 : 0.1;
            });
    } catch (e) { console.error("Sankey Layout Error:", e); }
  }, [variantData, predictedVoteShares, selectedNodeIndex, totalPopulation]);

  const isFlippedX = tooltip && (tooltip.x > (containerDim.width - 250));
  const isFlippedY = tooltip && (tooltip.y > (containerDim.height - 150));

  return (
    <div className="relative w-full" ref={containerRef}>
        <svg ref={svgRef} className="w-full h-[650px]"></svg>
        <AnimatePresence>
            {tooltip && (
                <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.1 }}
                    className="absolute z-50 pointer-events-none px-4 py-3 bg-slate-900/95 border border-slate-600 rounded-lg shadow-2xl backdrop-blur-sm text-sm text-white min-w-[200px]"
                    style={{ left: isFlippedX ? tooltip.x - 15 : tooltip.x + 15, x: isFlippedX ? "-100%" : "0%", top: isFlippedY ? tooltip.y - 15 : tooltip.y + 15, y: isFlippedY ? "-100%" : "0%" }}
                >
                    {tooltip.content}
                </motion.div>
            )}
        </AnimatePresence>
    </div>
  );
};

const MobilisationMatrix = ({ filteredVariants }) => {
  const channelEffectiveness = useMemo(() => {
    const channelCounts = {};
    let totalWeight = 0;

    filteredVariants.forEach(variant => {
      const pop = variant.total_population || 10;
      
      // FIX: Robust check for mobilisers from different possible locations in the object structure
      // Priority 1: media_ecosystem.primary_channels (from JSON schema)
      // Priority 2: primary_mobilisers (legacy)
      let mobilisers = variant.static_profile_data?.media_ecosystem?.primary_channels;
      
      if (!mobilisers || mobilisers.length === 0) {
           mobilisers = variant.static_profile_data?.primary_mobilisers;
      }

      if (mobilisers && Array.isArray(mobilisers)) {
          mobilisers.forEach(channel => {
             if (!channelCounts[channel]) channelCounts[channel] = 0;
             // Weight by population so larger segments dominate the matrix
             channelCounts[channel] += pop;
             totalWeight += pop;
          });
      }
    });

    return Object.entries(channelCounts)
        .map(([channel, score]) => ({ channel, score, pct: score / (totalWeight || 1) }))
        .sort((a, b) => b.score - a.score)
        .slice(0, 6); 
  }, [filteredVariants]);

  if (channelEffectiveness.length === 0) return <div className="text-slate-500 text-xs italic p-4 text-center">No channel data available.</div>;

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
       {channelEffectiveness.map((c, i) => (
         <div key={i} className="bg-slate-900/40 border border-slate-700 p-3 rounded-lg flex flex-col justify-between relative overflow-hidden group">
            <div className="absolute bottom-0 left-0 h-1 bg-emerald-500/50 transition-all duration-1000" style={{ width: `${c.pct * 500}%` }} />
            <div className="flex justify-between items-start mb-2"><span className="text-[10px] uppercase font-bold text-slate-500 tracking-wider">Priority #{i+1}</span><Megaphone size={12} className="text-emerald-400 opacity-60" /></div>
            <div className="font-bold text-slate-200 text-sm break-words leading-tight">{c.channel}</div>
         </div>
       ))}
    </div>
  );
};

const StructuralFracture = () => {
  const factors = useMemo(() => {
     let allFactors = [];
     Object.values(COHORT_LOOKUP).forEach(cohort => {
        const data = Array.isArray(cohort) ? cohort : [cohort];
        data.forEach(seg => {
           if (seg.segment_name === "CRITICAL_2026_CROSSCUTTING_FACTORS" && seg.factors) {
              allFactors = [...allFactors, ...seg.factors];
           }
        });
     });
     const unique = [];
     const map = new Map();
     for (const item of allFactors) {
         if(!map.has(item.factor_name)){ map.set(item.factor_name, true); unique.push(item); }
     }
     return unique.slice(0, 3); 
  }, []);
  if (!factors.length) return <div className="text-slate-500 text-xs italic">No structural data available.</div>;
  return (
    <div className="space-y-4">
       {factors.map((f, i) => (
         <div key={i} className="flex gap-4 items-start group">
            <div className="mt-1 p-1.5 rounded bg-slate-800 text-slate-400 border border-slate-700 group-hover:text-amber-400 group-hover:border-amber-500/50 transition-colors"><Scale size={14} /></div>
            <div><h4 className="text-xs font-bold text-slate-300 uppercase tracking-wide mb-1 flex items-center gap-2">{f.factor_name}</h4><p className="text-xs text-slate-400 leading-relaxed border-l-2 border-slate-700 pl-3 group-hover:border-amber-500/50 transition-colors">"{f.evidence || f.impact}"</p></div>
         </div>
       ))}
    </div>
  );
};

const ScenarioWarRoom = () => {
  const scenarios = useMemo(() => {
    let all = [];
    Object.values(COHORT_LOOKUP).forEach(cohort => {
      const data = Array.isArray(cohort) ? cohort : [cohort];
      data.forEach(segment => { if (segment.segment_name === "2026_STRATEGIC_SCENARIOS" && segment.scenarios) { all = [...all, ...segment.scenarios]; } });
    });
    return all.slice(0, 4); 
  }, []);
  if (!scenarios.length) return null;
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
       {scenarios.map((scen, i) => (
         <div key={i} className="bg-slate-900/50 border border-slate-700/50 p-4 rounded-xl relative overflow-hidden group hover:border-red-500/30 transition-colors">
            <div className="absolute top-0 right-0 p-3 opacity-10 group-hover:opacity-20"><AlertTriangle size={40} className="text-red-500" /></div>
            <div className="flex justify-between items-start mb-2"><span className="text-[10px] font-bold text-slate-500 uppercase tracking-widest bg-slate-800 px-2 py-0.5 rounded">Probability: {scen.probability_assessment}</span></div>
            <h4 className="text-sm font-bold text-white mb-2 group-hover:text-red-400 transition-colors">{scen.scenario_name}</h4>
            <p className="text-xs text-slate-400 leading-relaxed mb-3 line-clamp-3">{scen.outcome_dynamics || scen.outcome}</p>
         </div>
       ))}
    </div>
  );
};

const DiamondCard = ({ target, userParty }) => {
  const partyStyle = userParty && PARTY_CONFIG[userParty.toLowerCase()] ? PARTY_CONFIG[userParty.toLowerCase()] : PARTY_CONFIG.neutral;
  const score = target.partisanNCP || target.leverageScore || 0;
  return (
    <motion.div whileHover={{ y: -4 }} className={`relative group bg-slate-800/40 border border-slate-700 hover:border-${partyStyle.color.split('-')[1]}-500/50 rounded-xl p-5 transition-all duration-300 overflow-hidden`}>
      <div className={`absolute top-0 left-0 w-1 h-full ${partyStyle.bg}`} />
      <div className="absolute -right-6 -top-6 opacity-5 group-hover:opacity-10 transition-opacity"><Gem size={100} /></div>
      <div className="relative z-10">
        <div className="flex justify-between items-start mb-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              {target.dType.includes('LOCK') && <Anchor size={14} className="text-emerald-400" />}
              {target.dType.includes('TARGET') && <Crosshair size={14} className="text-amber-400" />}
              {target.dType.includes('RISK') && <Zap size={14} className="text-rose-400" />}
              {target.dType === 'VOLATILE' && <Activity size={14} className="text-purple-400" />}
              <span className={`text-[10px] font-black uppercase tracking-widest ${target.dType.includes('LOCK') ? 'text-emerald-400' : target.dType.includes('TARGET') ? 'text-amber-400' : target.dType.includes('RISK') ? 'text-rose-400' : 'text-slate-400'}`}>{target.dType}</span>
            </div>
            <h4 className="text-lg font-bold text-white leading-tight max-w-[80%]">{target.name}</h4>
          </div>
          <div className="text-right"><div className="text-xs text-slate-500 font-bold uppercase">{userParty ? 'NCP Score' : 'Leverage'}</div><div className={`text-2xl font-black ${partyStyle.color}`}>{score.toFixed(0)}</div></div>
        </div>
        <div className="grid grid-cols-2 gap-4 pt-4 border-t border-white/5">
          <div><div className="text-[10px] text-slate-500 uppercase mb-1">Population</div><div className="text-sm font-bold text-white font-mono">{(target.total_population || target.estimated_population || 0).toLocaleString()}</div></div>
          <div><div className="text-[10px] text-slate-500 uppercase mb-1">Persuadability</div><div className="text-sm font-bold text-white font-mono">{((target.campaign_targeting?.persuadability_score || 0) * 100).toFixed(0)}%</div></div>
        </div>
      </div>
    </motion.div>
  );
};

const CohortSelectorCard = ({ ageKey, data, totalPop, grandTotalPop, isActive, onClick }) => {
    const safeData = Array.isArray(data) ? data[0] : data;
    // Fallback logic to get a nice title if cultural_designator is missing
    const title = safeData?.cultural_designator || safeData?.segment_name || "Cohort Segment";
    const percent = Math.round((totalPop / (grandTotalPop || 1)) * 100);
    
    // Dynamic gradient based on selection state
    const barGradient = isActive 
        ? "bg-gradient-to-r from-indigo-500 to-purple-500" 
        : "bg-gradient-to-r from-blue-500 to-indigo-600 opacity-80";

    return (
        <motion.div 
            onClick={onClick} 
            whileHover={{ y: -4 }} 
            className={`
                min-w-[150px] flex-1 p-4 rounded-xl cursor-pointer border transition-all duration-300 
                relative overflow-hidden flex flex-col justify-between h-[130px]
                ${isActive 
                    ? 'bg-indigo-900/20 border-indigo-500 shadow-[0_0_20px_rgba(99,102,241,0.15)]' 
                    : 'bg-slate-900/40 border-slate-700/50 hover:border-slate-500'
                }
            `}
        >
            {isActive && <div className="absolute top-0 left-0 w-full h-1 bg-indigo-500" />}
            
            <div>
                <div className="flex justify-between items-start mb-1">
                    <span className={`text-[10px] font-black uppercase tracking-widest ${isActive ? 'text-indigo-400' : 'text-slate-500'}`}>
                        {ageKey}
                    </span>
                    {isActive && <div className="w-1.5 h-1.5 bg-indigo-400 rounded-full animate-pulse" />}
                </div>
                <h4 className={`text-xs font-bold leading-tight line-clamp-2 ${isActive ? 'text-white' : 'text-slate-300'}`}>
                    {title}
                </h4>
            </div>

            <div>
                <div className="flex justify-between items-end mb-1">
                    <span className="text-[9px] text-slate-500 font-medium uppercase">Voters</span>
                    <span className="text-[10px] font-mono font-bold text-white">{totalPop.toLocaleString()}</span>
                </div>
                <div className="w-full bg-slate-800 h-1.5 rounded-full overflow-hidden">
                    <div className={`h-full transition-all duration-1000 ${barGradient}`} style={{ width: `${percent}%` }} />
                </div>
            </div>
        </motion.div>
    );
};

// --- UPDATED CAMPAIGN HEATMAP (HEAVY INTELLIGENCE OVERHAUL) ---
const CampaignHeatmap = ({ genderAgeSummary, activeGender, variants, cohortLookup }) => {
  const [selectedAge, setSelectedAge] = useState(null);
  const maxPop = Math.max(...Object.values(genderAgeSummary).map(d => d.total_population), 1);

  // Helper to extract rich cohort data from JSON
  const getCohortRichData = (ageKey) => {
      const rawData = cohortLookup[ageKey];
      const dataObj = Array.isArray(rawData) ? rawData[0] : rawData;
      return dataObj?.cohort_data || null;
  };

  // Helper to get tactical data (Profiles, Channels) for Selected Age
  const getTacticalData = (targetVariants) => {
      // Top 3 Profiles by Population
      const profiles = [...targetVariants]
          .sort((a,b) => b.total_population - a.total_population)
          .slice(0, 3);
      
      // Aggregated Channels (Using media_ecosystem.primary_channels as fallback)
      const channelCounts = {};
      targetVariants.forEach(v => {
          const mobs = v.static_profile_data?.primary_mobilisers || v.static_profile_data?.media_ecosystem?.primary_channels || [];
          mobs.forEach(m => {
              channelCounts[m] = (channelCounts[m] || 0) + (v.total_population || 0);
          });
      });
      const topChannels = Object.entries(channelCounts)
          .sort(([,a], [,b]) => b - a)
          .slice(0, 3)
          .map(([k]) => k);

      return { profiles, topChannels };
  };

  // Memoized aggregator for the Default View
  const campaignWidePulse = useMemo(() => {
      let totalPop = 0;
      let weightedTurnout = 0;
      const genderCounts = { male: 0, female: 0 };
      const partyCounts = {};

      Object.values(genderAgeSummary).forEach(s => {
          totalPop += s.total_population;
          weightedTurnout += (s.avg_turnout * s.total_population);
          genderCounts.male += s.male_count;
          genderCounts.female += s.female_count;
          
          Object.entries(s.party_counts).forEach(([p, count]) => {
              partyCounts[p] = (partyCounts[p] || 0) + count;
          });
      });

      let dominantParty = "Neutral";
      let maxScore = 0;
      Object.entries(partyCounts).forEach(([p, count]) => {
          if(count > maxScore) { maxScore = count; dominantParty = p; }
      });

      // Get Global Top Variants
      const topGlobalVariants = [...variants]
          .sort((a,b) => b.total_population - a.total_population)
          .slice(0, 3);

      return {
          turnout: totalPop > 0 ? weightedTurnout / totalPop : 0,
          malePct: totalPop > 0 ? genderCounts.male / totalPop : 0.5,
          femalePct: totalPop > 0 ? genderCounts.female / totalPop : 0.5,
          dominantParty,
          topVariants: topGlobalVariants
      };
  }, [genderAgeSummary, variants]);

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-end mb-2">
          <h4 className="text-xs font-bold text-slate-400 uppercase tracking-widest">Demographic Density Matrix</h4>
          <div className="flex gap-2 text-[10px] text-slate-500">
              <span className="flex items-center"><div className="w-2 h-2 bg-blue-500 rounded-sm mr-1"></div> Male</span>
              <span className="flex items-center"><div className="w-2 h-2 bg-pink-500 rounded-sm mr-1"></div> Female</span>
          </div>
      </div>
      
      {/* 1. VISUAL GRID */}
      <div className="grid grid-cols-7 gap-1 h-36">
        {AGE_BUCKETS.map(age => {
          const stats = genderAgeSummary[age] || { total_population: 0, male_pct: 0.5, female_pct: 0.5, avg_turnout: 0 };
          const intensity = stats.total_population / maxPop; 
          const maleIntensity = intensity * stats.male_pct;
          const femaleIntensity = intensity * stats.female_pct;
          const isSelected = selectedAge === age;

          return (
            <div 
                key={age} 
                className={`flex flex-col gap-0.5 group relative cursor-pointer transition-all ${isSelected ? 'ring-1 ring-white/50 rounded-lg p-0.5 scale-[1.02] z-10' : ''}`}
                onClick={() => setSelectedAge(isSelected ? null : age)}
            >
              <div className="text-[10px] font-bold text-slate-500 text-center mb-1">{age}</div>
              
              {/* Stacked Bar Container */}
              <div className="flex-1 w-full bg-slate-800/50 rounded-sm relative overflow-hidden border border-slate-700/30 flex flex-col">
                  
                  {/* Female Segment (Top) */}
                  <div className="flex-1 flex flex-col justify-end relative">
                        <motion.div 
                            initial={{ height: 0 }} 
                            animate={{ height: activeGender === 'male' ? 0 : `${femaleIntensity * 100}%` }} 
                            className={`w-full ${activeGender === 'male' ? 'bg-transparent' : 'bg-pink-500/60'} ${isSelected ? 'bg-pink-500' : 'group-hover:bg-pink-500/80'} transition-colors relative`}
                        >
                            {(activeGender === 'all' || activeGender === 'female') && femaleIntensity > 0.1 && (
                                <span className="absolute bottom-1 left-1/2 -translate-x-1/2 text-[9px] font-bold text-pink-100 drop-shadow-md">
                                    {(stats.female_pct * 100).toFixed(0)}%
                                </span>
                            )}
                        </motion.div>
                  </div>

                  {/* Male Segment (Bottom) */}
                  <div className="flex-1 flex flex-col justify-start relative">
                        <motion.div 
                            initial={{ height: 0 }} 
                            animate={{ height: activeGender === 'female' ? 0 : `${maleIntensity * 100}%` }} 
                            className={`w-full ${activeGender === 'female' ? 'bg-transparent' : 'bg-blue-500/60'} ${isSelected ? 'bg-blue-500' : 'group-hover:bg-blue-500/80'} transition-colors relative`}
                        >
                             {(activeGender === 'all' || activeGender === 'male') && maleIntensity > 0.1 && (
                                <span className="absolute top-1 left-1/2 -translate-x-1/2 text-[9px] font-bold text-blue-100 drop-shadow-md">
                                    {(stats.male_pct * 100).toFixed(0)}%
                                </span>
                            )}
                        </motion.div>
                  </div>
                  
                  {/* Turnout Overlay (Texture) */}
                  <div 
                    className="absolute inset-0 pointer-events-none opacity-20"
                    style={{ backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 2px, #000 2px, #000 4px)' }} 
                  />
              </div>
              
              {/* Total Pop Label */}
              <div className="text-[9px] text-slate-500 text-center font-mono mt-1">
                  {(stats.total_population / 1000).toFixed(1)}k
              </div>
            </div>
          );
        })}
      </div>

      {/* 2. EXPANDED INTELLIGENCE PANEL */}
      <AnimatePresence mode="wait">
          {selectedAge ? (
              // --- SELECTED DEEP DIVE VIEW (MERGED INTELLIGENCE) ---
              <motion.div
                key="selected"
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="overflow-hidden"
              >
                  {(() => {
                      const richData = getCohortRichData(selectedAge);
                      const stats = genderAgeSummary[selectedAge];
                      
                      // Filter variants for tactical column based on selected Age
                      const bucketVariants = variants.filter(v => {
                          if(selectedAge === v.age_range) return true;
                          const avgAge = v.avg_age || 40;
                          if(selectedAge === '18-24') return avgAge < 25;
                          if(selectedAge === '25-34') return avgAge >= 25 && avgAge < 35;
                          if(selectedAge === '35-44') return avgAge >= 35 && avgAge < 45;
                          if(selectedAge === '45-54') return avgAge >= 45 && avgAge < 55;
                          if(selectedAge === '55-64') return avgAge >= 55 && avgAge < 65;
                          if(selectedAge === '64-75') return avgAge >= 65 && avgAge < 76;
                          if(selectedAge === '75+')   return avgAge >= 76;
                          return false;
                      });
                      const { profiles, topChannels } = getTacticalData(bucketVariants);
                      
                      return (
                        <div className="bg-slate-800/40 rounded-lg p-5 border border-slate-600/50 mt-2 grid grid-cols-12 gap-6 relative">
                            <div className="absolute top-0 left-6 right-6 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />
                            
                            {/* COL 1: NARRATIVE CORE (The "Why") - 7 Cols */}
                            <div className="col-span-12 lg:col-span-7 flex flex-col gap-4 border-r border-slate-700/50 pr-6">
                                <div>
                                    <h4 className="text-sm font-bold text-white mb-2 flex items-center gap-2">
                                        <Split size={14} className="text-indigo-400"/>
                                        {richData?.summary_header || "Cohort Analysis"}
                                    </h4>
                                    <p className="text-xs text-slate-300 leading-relaxed">
                                        {richData?.cohort_analysis || "No detailed narrative analysis available for this cohort."}
                                    </p>
                                </div>
                                
                                <div className="grid grid-cols-2 gap-4">
                                    {/* Psychometric Pulse */}
                                    <div className="space-y-3">
                                        <div className="text-[10px] uppercase text-slate-500 font-bold tracking-widest flex items-center gap-2">
                                            <Zap size={12} className="text-amber-400" /> Dominant Tension
                                        </div>
                                        <div className="text-xs text-white italic pl-3 border-l-2 border-amber-500/50">
                                            "{richData?.dominant_tension || "N/A"}"
                                        </div>
                                        
                                        <div className="space-y-2 mt-2">
                                            {richData?.generational_baseline_metrics && Object.entries(richData.generational_baseline_metrics).slice(0,2).map(([key, val]) => (
                                                <div key={key}>
                                                    <div className="flex justify-between text-[9px] text-slate-400 uppercase mb-0.5">
                                                        <span>{key.replace(/_/g, ' ')}</span>
                                                        <span>{(val * 100).toFixed(0)}%</span>
                                                    </div>
                                                    <div className="w-full bg-slate-700 h-1 rounded-full overflow-hidden">
                                                        <div className="bg-slate-400 h-1 rounded-full" style={{ width: `${val * 100}%` }} />
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Gender Divergence */}
                                    <div className="flex flex-col gap-2">
                                        <div className="bg-slate-700/30 p-2.5 rounded border-l-2 border-pink-500">
                                            <div className="text-[9px] text-slate-400 uppercase tracking-wider mb-1">Female Lean</div>
                                            <div className="text-[10px] text-white font-medium leading-tight">{richData?.gender_divergence_analysis?.female_cohort_lean || "N/A"}</div>
                                        </div>
                                        <div className="bg-slate-700/30 p-2.5 rounded border-l-2 border-blue-500">
                                            <div className="text-[9px] text-slate-400 uppercase tracking-wider mb-1">Male Lean</div>
                                            <div className="text-[10px] text-white font-medium leading-tight">{richData?.gender_divergence_analysis?.male_cohort_lean || "N/A"}</div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* COL 2: TACTICAL TARGETS (The "Who" & "How") - 5 Cols */}
                            <div className="col-span-12 lg:col-span-5 pl-2 space-y-5">
                                {/* Top Profiles */}
                                <div>
                                    <div className="text-[10px] uppercase text-slate-500 font-bold tracking-widest mb-2 flex items-center gap-2">
                                        <Users size={12} /> Key Profiles
                                    </div>
                                    <div className="space-y-2">
                                        {profiles.map((p, i) => (
                                            <div key={i} className="flex justify-between items-center text-xs border-b border-slate-700/50 pb-1 last:border-0">
                                                <span className="text-white font-medium truncate max-w-[140px]">{p.name}</span>
                                                <span className="text-slate-400 font-mono text-[10px]">{(p.total_population / 1000).toFixed(1)}k</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* Channels */}
                                <div>
                                    <div className="text-[10px] uppercase text-slate-500 font-bold tracking-widest mb-2 flex items-center gap-2">
                                        <Megaphone size={12} /> Priority Channels
                                    </div>
                                    <div className="flex flex-wrap gap-1.5">
                                        {topChannels.length > 0 ? topChannels.map(c => (
                                            <span key={c} className="text-[10px] px-2 py-0.5 bg-slate-700 text-slate-300 rounded border border-slate-600">
                                                {c}
                                            </span>
                                        )) : <span className="text-[10px] text-slate-500 italic">No channel data available.</span>}
                                    </div>
                                </div>

                                {/* Key Metric Badges */}
                                <div className="grid grid-cols-2 gap-2 pt-1">
                                    <div className="px-2 py-1.5 rounded bg-slate-700/30 border border-slate-600/50 text-center">
                                        <div className="text-[9px] text-slate-400 uppercase mb-0.5">Turnout</div>
                                        <div className="text-sm font-bold text-white">{(stats.avg_turnout * 100).toFixed(0)}%</div>
                                    </div>
                                    <div className="px-2 py-1.5 rounded bg-slate-700/30 border border-slate-600/50 text-center">
                                        <div className="text-[9px] text-slate-400 uppercase mb-0.5">Affinity</div>
                                        <div className="text-sm font-bold truncate px-1" style={{color: getPartyHex(stats.dominant_party)}}>{stats.dominant_party}</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                      );
                  })()}
              </motion.div>
          ) : (
              // --- DEFAULT AGGREGATE VIEW (WHOLE ELECTORATE STRATEGY) ---
              <motion.div
                key="default"
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="overflow-hidden"
              >
                  <div className="bg-slate-800/40 rounded-lg p-5 border border-slate-600/50 mt-2 grid grid-cols-1 md:grid-cols-3 gap-6 relative">
                      {/* COL 1: CAMPAIGN PULSE */}
                      <div className="flex flex-col justify-center space-y-4">
                          <div className="flex items-center gap-3 mb-2">
                              <div className="p-2 bg-indigo-500/10 rounded-full border border-indigo-500/20">
                                  <Activity size={20} className="text-indigo-400" />
                              </div>
                              <div>
                                  <h4 className="text-xs font-bold text-white uppercase tracking-wider">Campaign-Wide Pulse</h4>
                                  <div className="text-[10px] text-slate-400">Aggregated from calibrated polling.</div>
                              </div>
                          </div>
                          
                          <div className="grid grid-cols-2 gap-4">
                              <div>
                                  <div className="text-2xl font-black text-white">{(campaignWidePulse.turnout * 100).toFixed(1)}%</div>
                                  <div className="text-[9px] uppercase text-slate-500 tracking-widest font-bold">Avg. Turnout</div>
                              </div>
                              <div>
                                  <div className="text-2xl font-black text-slate-300">
                                      <span className="text-blue-400">{(campaignWidePulse.malePct * 100).toFixed(0)}</span>
                                      <span className="text-slate-600 mx-1">/</span>
                                      <span className="text-pink-400">{(campaignWidePulse.femalePct * 100).toFixed(0)}</span>
                                  </div>
                                  <div className="text-[9px] uppercase text-slate-500 tracking-widest font-bold">M / F Split</div>
                              </div>
                          </div>
                      </div>

                      {/* COL 2: TOP STRATEGIC GROUPS */}
                      <div className="border-l border-slate-700/50 pl-6">
                          <div className="text-[10px] uppercase text-slate-500 font-bold tracking-widest mb-3 flex items-center gap-2">
                              <Target size={12} /> Dominant Voter Segments
                          </div>
                          <div className="space-y-2">
                              {campaignWidePulse.topVariants.map((v, i) => (
                                  <div key={i} className="flex justify-between items-center text-xs">
                                      <span className="text-white font-medium truncate max-w-[140px]">{v.name}</span>
                                      <span className="text-slate-400 font-mono">{(v.total_population / 1000).toFixed(1)}k</span>
                                  </div>
                              ))}
                          </div>
                      </div>

                      {/* COL 3: LEADING PARTY */}
                      <div className="border-l border-slate-700/50 pl-6 flex flex-col justify-center text-center">
                          <div className="text-[10px] uppercase text-slate-500 font-bold tracking-widest mb-2">
                              Projected Winner
                          </div>
                          <div className="text-3xl font-black mb-1" style={{ color: getPartyHex(campaignWidePulse.dominantParty) }}>
                              {campaignWidePulse.dominantParty}
                          </div>
                          <div className="text-[9px] text-slate-400">
                              Based on current variant distribution.
                          </div>
                      </div>
                  </div>
              </motion.div>
          )}
      </AnimatePresence>
    </div>
  );
};

// --- NEW VARIANT PROFILE CARD (For Drill Down) ---
const VariantProfileCard = ({ variant, calibratedVariant }) => {
    const staticData = variant.static_profile_data || {};
    const propensities = calibratedVariant?.political_profile?.party_propensity || {};
    const metrics = staticData.strategic_targeting_metrics || {};
    const cog = staticData.cognitive_architecture || {};
    
    return (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 h-full animate-in fade-in slide-in-from-bottom-4">
            {/* Left Col: Profile Core */}
            <div className="lg:col-span-4 flex flex-col gap-4">
                 <div className="bg-slate-900/40 border border-slate-700/50 rounded-xl p-6 flex flex-col gap-4">
                     <div>
                         <div className="flex justify-between items-start mb-2">
                            <span className="text-xs font-bold text-slate-400 uppercase tracking-wide bg-slate-800 px-2 py-0.5 rounded-full">{variant.age_range}</span>
                            <ValueBadge value={staticData.archetype_confidence || "HIGH"} trend="positive" />
                         </div>
                         <h2 className="text-2xl font-bold text-white leading-tight">{variant.name}</h2>
                         <p className="text-sm text-sky-400 mt-1 font-medium">{staticData.political_leaning}</p>
                     </div>
                    
                    <div className="text-xs text-slate-300 leading-relaxed border-t border-slate-700/50 pt-4">
                        {staticData.paragraph_summary || "No summary available."}
                    </div>

                    <div className="grid grid-cols-2 gap-4 pt-2">
                        <div>
                            <div className="text-[10px] text-slate-500 uppercase">VALS Type</div>
                            <div className="text-sm text-white font-bold">{metrics.vals_segment || "N/A"}</div>
                        </div>
                         <div>
                            <div className="text-[10px] text-slate-500 uppercase">Activation Difficulty</div>
                            <div className="text-sm text-white font-bold">{metrics.activation_difficulty || "N/A"}</div>
                        </div>
                    </div>
                 </div>

                 <Panel title="Cognitive Architecture" icon={Brain}>
                     <div className="space-y-3">
                        <div className="flex justify-between text-xs">
                            <span className="text-slate-400">Need for Closure</span>
                            <span className="text-white text-right max-w-[60%]">{cog.need_for_closure || "N/A"}</span>
                        </div>
                        <div className="flex justify-between text-xs">
                            <span className="text-slate-400">System Justification</span>
                            <span className="text-white text-right">{cog.system_justification ? (cog.system_justification * 100).toFixed(0) + '%' : "N/A"}</span>
                        </div>
                        <div className="flex justify-between text-xs">
                            <span className="text-slate-400">Messaging Trigger</span>
                            <span className="text-emerald-400 text-right font-medium">{cog.messaging_trigger || "N/A"}</span>
                        </div>
                     </div>
                 </Panel>
            </div>

            {/* Middle Col: Cultural & Narrative */}
            <div className="lg:col-span-4 flex flex-col gap-4">
                <Panel title="Cultural Habitus" icon={ShoppingBag}>
                    <div className="space-y-4">
                         <div>
                             <div className="text-[10px] text-slate-500 uppercase mb-1">Consumption Style</div>
                             <div className="text-xs text-white">{staticData.cultural_habitus?.consumption_style || "N/A"}</div>
                         </div>
                         <div>
                             <div className="text-[10px] text-slate-500 uppercase mb-1">Digital Home</div>
                             <div className="text-xs text-white">{staticData.cultural_habitus?.digital_home || "N/A"}</div>
                         </div>
                         <div>
                             <div className="text-[10px] text-slate-500 uppercase mb-2">Key Brands</div>
                             <div className="flex flex-wrap gap-2">
                                 {staticData.cultural_habitus?.key_brands?.map(b => (
                                     <span key={b} className="text-[10px] bg-slate-700 px-2 py-0.5 rounded text-slate-300 border border-slate-600">{b}</span>
                                 ))}
                             </div>
                         </div>
                    </div>
                </Panel>

                <Panel title="Sociolect Markers" icon={Hash}>
                    <div className="flex flex-wrap gap-2">
                        {staticData.sociolect_markers?.map(m => (
                            <div key={m} className="bg-slate-800 border border-slate-600 rounded-lg px-3 py-2 text-xs italic text-slate-300 relative">
                                <span className="absolute -top-1 -left-1 text-[8px] text-slate-500">“</span>
                                {m}
                            </div>
                        ))}
                    </div>
                </Panel>
            </div>

            {/* Right Col: Political & Red Lines */}
            <div className="lg:col-span-4 flex flex-col gap-4">
                <Panel title="Electoral Affinity (Calibrated)" icon={Activity}>
                     <div className="space-y-3">
                         {Object.entries(propensities)
                            .sort(([,a], [,b]) => b - a)
                            .map(([party, score]) => (
                                <div key={party}>
                                    <div className="flex justify-between text-xs mb-1">
                                        <span className="font-bold text-slate-300 uppercase">{party.replace('_', ' ').replace('uk', '')}</span>
                                        <span className="font-mono text-slate-400">{(score * 100).toFixed(1)}%</span>
                                    </div>
                                    <div className="w-full bg-slate-800 rounded-full h-2 overflow-hidden">
                                        <div className={`h-full ${getPartyColor(party)}`} style={{ width: `${score * 100}%` }} />
                                    </div>
                                </div>
                            ))}
                     </div>
                </Panel>

                <Panel title="Policy Red Lines (2026)" icon={Lock}>
                    <ul className="space-y-2">
                        {staticData['2026_policy_red_lines']?.map((line, i) => (
                            <li key={i} className="flex items-start gap-2 text-xs text-rose-300">
                                <X size={12} className="mt-0.5 flex-shrink-0" />
                                {line}
                            </li>
                        ))}
                    </ul>
                </Panel>
            </div>
        </div>
    );
};

// --- COHORT DETAIL VIEW (REFACTORED WITH CHIP NAV & FILTERING) ---
const CohortDetailView = ({ ageKey, staticData, variantData, voteShares, totalPopulation }) => {
    const safeData = Array.isArray(staticData) ? staticData[0] : staticData;
    const [selectedVariantName, setSelectedVariantName] = useState('all');

    // 1. CALIBRATE VARIANTS (Hydraulic Logic v2 - Normalized)
    const calibratedVariants = useMemo(() => {
        return getCalibratedVariants(variantData, voteShares, totalPopulation);
    }, [variantData, voteShares, totalPopulation]);

    const profiles = safeData?.profiles ? Object.values(safeData.profiles) : [];
    const context = safeData?.segment_context || "No qualitative intelligence available.";
    
    // Sort profiles by population for the chip list AND FILTER to only include those in DB return
    const sortedProfiles = useMemo(() => {
        const variantsInDB = variantData.map(v => v.name);
        return [...profiles]
            .filter(p => variantsInDB.includes(p.display_name)) // FILTER: Only show what we have data for
            .sort((a, b) => {
                const popA = variantData.find(v => v.name === a.display_name)?.total_population || 0;
                const popB = variantData.find(v => v.name === b.display_name)?.total_population || 0;
                return popB - popA;
            });
    }, [profiles, variantData]);

    const activeVariant = selectedVariantName !== 'all' 
        ? variantData.find(v => v.name === selectedVariantName) 
        : null;

    const activeCalibratedVariant = selectedVariantName !== 'all'
        ? calibratedVariants.find(v => v.name === selectedVariantName)
        : null;

    return (
        <div className="space-y-6 min-h-[600px]">
            {/* VARIANT NAVIGATION (CHIP TAB) */}
            <div className="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-thin scrollbar-thumb-slate-700 scrollbar-track-transparent">
                <button
                    onClick={() => setSelectedVariantName('all')}
                    className={`px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider whitespace-nowrap transition-all border ${
                        selectedVariantName === 'all' 
                        ? 'bg-indigo-600 border-indigo-500 text-white shadow-md' 
                        : 'bg-slate-800 border-slate-700 text-slate-400 hover:bg-slate-700 hover:text-white'
                    }`}
                >
                    All Variants
                </button>
                {sortedProfiles.map(p => (
                    <button
                        key={p.display_name}
                        onClick={() => setSelectedVariantName(p.display_name)}
                        className={`px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider whitespace-nowrap transition-all border ${
                            selectedVariantName === p.display_name 
                            ? 'bg-sky-600 border-sky-500 text-white shadow-md' 
                            : 'bg-slate-800 border-slate-700 text-slate-400 hover:bg-slate-700 hover:text-white'
                        }`}
                    >
                        {p.display_name}
                    </button>
                ))}
            </div>

            {/* CONDITIONAL CONTENT AREA */}
            <AnimatePresence mode="wait">
                {selectedVariantName === 'all' ? (
                    // AGGREGATE COHORT VIEW
                    <motion.div 
                        key="aggregate"
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: 20 }}
                        className="grid grid-cols-1 xl:grid-cols-12 gap-6"
                    >
                        <div className="xl:col-span-4 flex flex-col gap-6">
                            <Panel title="Cohort Intelligence" icon={Lightbulb} className="flex-1 border-t-4 border-t-white">
                                <IntelligenceDashboard 
                                    context={context} 
                                    intelligence={profiles[0]?.intelligence_summary}
                                />
                            </Panel>
                            <Panel title="Local Concerns" icon={AlertCircle} className="flex-shrink-0 border-t-4 border-t-white">
                                <ReactiveIssueHeatmap profiles={profiles} variantData={variantData} />
                            </Panel>
                        </div>
                        
                        <div className="xl:col-span-4 flex flex-col gap-6">
                            <Panel title="Gender Divergence" icon={GitPullRequest} className="flex-1 border-t-4 border-t-white">
                                <GenderPolarizationChart data={calibratedVariants} />
                            </Panel>
                            <Panel title="Dominant Media Ecosystem" icon={Radio} className="flex-shrink-0 border-t-4 border-t-white">
                                <MediaEcosystemBubble variantData={calibratedVariants} />
                            </Panel>
                        </div>

                        <div className="xl:col-span-4 space-y-6">
                             {/* STRATEGIC FRICTION POINTS (REPLACED PREVALENCE) */}
                             <div className="bg-slate-900/40 border border-slate-700/50 rounded-2xl p-4 border-t-4 border-t-white h-full">
                                <div className="flex items-center justify-between mb-4">
                                    <h3 className="text-xs font-bold text-slate-500 uppercase tracking-widest flex items-center gap-2">
                                        <Gauge size={14} /> Strategic Friction Points
                                    </h3>
                                </div>
                                <div className="space-y-4">
                                    {/* Pull data from cohort_data if available */}
                                    {(() => {
                                        const cohortData = safeData.cohort_data || {};
                                        const metrics = cohortData.generational_baseline_metrics || {};
                                        
                                        return (
                                            <>
                                                {/* Dominant Tension */}
                                                <div className="bg-slate-800/50 p-3 rounded-lg border border-slate-700">
                                                    <div className="text-[10px] text-slate-400 uppercase mb-1">Dominant Tension</div>
                                                    <div className="text-xs text-white font-medium italic">
                                                        "{cohortData.dominant_tension || "Data unavailable"}"
                                                    </div>
                                                </div>

                                                {/* Metric Bars */}
                                                <div className="space-y-3 pt-2">
                                                    {Object.entries(metrics).map(([key, val]) => (
                                                        <div key={key}>
                                                            <div className="flex justify-between text-[10px] text-slate-400 uppercase mb-1">
                                                                <span>{key.replace(/_/g, ' ')}</span>
                                                                <span className="font-mono text-white">{(val * 100).toFixed(0)}%</span>
                                                            </div>
                                                            <div className="w-full bg-slate-800 h-1.5 rounded-full overflow-hidden">
                                                                <motion.div 
                                                                    initial={{ width: 0 }}
                                                                    animate={{ width: `${val * 100}%` }}
                                                                    transition={{ duration: 1, ease: "easeOut" }}
                                                                    className={`h-full rounded-full ${
                                                                        val > 0.7 ? 'bg-rose-500' : val > 0.4 ? 'bg-amber-500' : 'bg-emerald-500'
                                                                    }`} 
                                                                />
                                                            </div>
                                                        </div>
                                                    ))}
                                                    {Object.keys(metrics).length === 0 && (
                                                        <div className="text-xs text-slate-500 italic text-center py-4">
                                                            No psychometric baseline data found.
                                                        </div>
                                                    )}
                                                </div>

                                                {/* Political Implication */}
                                                {cohortData.gender_divergence_analysis?.political_implication && (
                                                    <div className="mt-4 pt-4 border-t border-slate-700/50">
                                                        <div className="flex items-start gap-2">
                                                            <Target size={14} className="text-indigo-400 mt-0.5 flex-shrink-0" />
                                                            <p className="text-[12px] text-slate-300 leading-snug">
                                                                <span className="font-bold text-indigo-400 uppercase">Implication: </span>
                                                                {cohortData.gender_divergence_analysis.political_implication}
                                                            </p>
                                                        </div>
                                                    </div>
                                                )}
                                            </>
                                        );
                                    })()}
                                </div>
                             </div>
                        </div>
                    </motion.div>
                ) : (
                    // SPECIFIC VARIANT VIEW (CARD STYLE)
                    <motion.div
                        key="variant"
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -20 }}
                        className="h-full"
                    >
                        {activeVariant && activeCalibratedVariant ? (
                            <VariantProfileCard 
                                variant={activeVariant} 
                                calibratedVariant={activeCalibratedVariant} 
                            />
                        ) : (
                            <div className="text-center p-12 text-slate-500">Variant data unavailable.</div>
                        )}
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

// --- NEW HEADER COMPONENT: SYNTHESIZED KPI HEADER ---
const DemographicKPIHeader = ({ totalPop, totalWards, genderStats, predictedVoteShares }) => {
    // Determine dominant party from predicted vote shares
    let dominantParty = "Neutral";
    let maxShare = 0;
    if (predictedVoteShares) {
        Object.entries(predictedVoteShares).forEach(([party, share]) => {
            if (share > maxShare && party !== 'other') {
                maxShare = share;
                dominantParty = normalizePartyKey(party);
            }
        });
    }

    return (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {/* Metric 1: Scale */}
            <Panel className="relative overflow-hidden group h-full">
                <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity">
                    <Users size={64} className="text-blue-400" />
                </div>
                <div className="flex flex-col h-full justify-between relative z-10">
                    <div>
                        <span className="text-xs font-bold text-blue-400 uppercase tracking-wider">Electorate Scale</span>
                        <div className="text-3xl font-bold text-white mt-1">{Math.round(totalPop).toLocaleString()}</div>
                        <div className="text-xs text-slate-400 mt-1">Active voters across {totalWards} wards</div>
                    </div>
                </div>
            </Panel>

            {/* Metric 2: Gender Balance */}
            <Panel className="relative overflow-hidden group h-full">
                <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity">
                    <PieChart size={64} className="text-purple-400" />
                </div>
                <div className="flex flex-col h-full justify-between relative z-10">
                    <div>
                        <span className="text-xs font-bold text-purple-400 uppercase tracking-wider">Gender Split</span>
                        <div className="flex items-center gap-3 mt-2">
                            <span className="text-lg font-bold text-blue-300 font-mono">{genderStats.malePct}% M</span>
                            <div className="w-24 h-2 bg-slate-800 rounded-full overflow-hidden flex relative">
                                <div className="bg-blue-500 h-full" style={{width: `${genderStats.malePct}%`}}/>
                                <div className="bg-pink-500 h-full" style={{width: `${genderStats.femalePct}%`}}/>
                            </div>
                            <span className="text-lg font-bold text-pink-300 font-mono">{genderStats.femalePct}% F</span>
                        </div>
                    </div>
                </div>
            </Panel>

            {/* Metric 3: Dominant Trend */}
            <Panel className="relative overflow-hidden group h-full">
                <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity">
                    <TrendingUp size={64} className="text-green-400" />
                </div>
                <div className="flex flex-col h-full justify-between relative z-10">
                    <div>
                        <span className="text-xs font-bold text-green-400 uppercase tracking-wider">Projected Lead</span>
                        <div className="text-3xl font-bold text-white mt-1" style={{ color: getPartyHex(dominantParty) }}>
                            {dominantParty}
                        </div>
                        <div className="text-xs text-slate-400 mt-1">Based on current calibrated polling</div>
                    </div>
                </div>
            </Panel>

            {/* Metric 4: Volatility */}
            <Panel className="relative overflow-hidden group h-full">
                <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity">
                    <Activity size={64} className="text-orange-400" />
                </div>
                <div className="flex flex-col h-full justify-between relative z-10">
                    <div>
                        <span className="text-xs font-bold text-orange-400 uppercase tracking-wider">Market Volatility</span>
                        <div className="text-xl font-bold text-white mt-1">Medium-High</div>
                        <div className="text-xs text-slate-400 mt-1">Significant churn in <span className="text-white">18-34</span> cohorts</div>
                    </div>
                </div>
            </Panel>
        </div>
    );
};

// ====================================================================================
// 7. MAIN EXPORT
// ====================================================================================

export default function DemographicsSuite({ summaryData, userParty, campaignData }) {
  const [activeGender, setActiveGender] = useState('all'); 
  const [selectedCohort, setSelectedCohort] = useState('25-34'); 

  const { totalPop, variants: hydratedVariants, genderAgeSummary } = useMemo(() => {
     return hydrateData(summaryData);
  }, [summaryData]);

  const predictedVoteShares = useMemo(() => {
      if (campaignData) {
          const predSummary = campaignData.predictionData?.summary || {};
          const swingSummary = campaignData.swingData?.summary || {};
          const merged = { ...swingSummary, ...predSummary };
          if (merged.vote_shares) return merged.vote_shares;
          if (campaignData.vote_shares) return campaignData.vote_shares;
      }
      if (summaryData) {
          if (summaryData.vote_shares) return summaryData.vote_shares;
          if (summaryData.summary && summaryData.summary.vote_shares) return summaryData.summary.vote_shares;
      }
      return null;
  }, [campaignData, summaryData]);

  const totalWards = summaryData?.total_wards_processed || 0;
  
  const filteredVariants = useMemo(() => {
     if (!hydratedVariants) return [];
     if (activeGender === 'all') return hydratedVariants;
     return hydratedVariants.filter(v => v.gender && v.gender.toLowerCase() === activeGender);
  }, [hydratedVariants, activeGender]);

  // Calibrated global variants for the Global Star Chart
  // FIX: Use filteredVariants here instead of hydratedVariants so chart reacts to gender toggle
  const globalCalibratedVariants = useMemo(() => {
      return getCalibratedVariants(filteredVariants, predictedVoteShares, totalPop);
  }, [filteredVariants, predictedVoteShares, totalPop]);

  // NEW: Recalculate demographic stats based on Calibrated Variants
  const calibratedGenderAgeSummary = useMemo(() => {
      const summary = {};
      AGE_BUCKETS.forEach(bucket => {
        summary[bucket] = { 
            total_population: 0, 
            male_pct: 0.5, 
            female_pct: 0.5, 
            male_count: 0, 
            female_count: 0,
            avg_turnout: 0,
            turnout_weighted_sum: 0,
            dominant_party: null,
            party_counts: {}
        };
      });

      const variantsToUse = globalCalibratedVariants.length > 0 ? globalCalibratedVariants : hydratedVariants;

      variantsToUse.forEach(v => {
          let bucket = '75+';
          if (AGE_BUCKETS.includes(v.age_range)) bucket = v.age_range;
          else if (v.avg_age) {
            if (v.avg_age < 25) bucket = '18-24';
            else if (v.avg_age < 35) bucket = '25-34';
            else if (v.avg_age < 45) bucket = '35-44';
            else if (v.avg_age < 55) bucket = '45-54';
            else if (v.avg_age < 65) bucket = '55-64';
            else if (v.avg_age < 75) bucket = '64-75';
          }

          if (summary[bucket]) {
              const pop = v.total_population || 0;
              summary[bucket].total_population += pop;
              
              if (v.gender === 'Male') summary[bucket].male_count += pop;
              else if (v.gender === 'Female') summary[bucket].female_count += pop;
              else {
                 summary[bucket].male_count += (pop/2);
                 summary[bucket].female_count += (pop/2);
              }

              // Weighted Turnout
              const turnout = v.base_turnout || v.campaign_targeting?.turnout_probability || 0.5;
              summary[bucket].turnout_weighted_sum += (turnout * pop);

              // Party Affinity
              const props = v.political_profile?.party_propensity || {};
              Object.entries(props).forEach(([party, score]) => {
                  const normParty = normalizePartyKey(party);
                  if (!summary[bucket].party_counts[normParty]) summary[bucket].party_counts[normParty] = 0;
                  summary[bucket].party_counts[normParty] += (score * pop);
              });
          }
      });

      Object.values(summary).forEach(s => {
          if (s.total_population > 0) {
              s.male_pct = s.male_count / s.total_population;
              s.female_pct = s.female_count / s.total_population;
              s.avg_turnout = s.turnout_weighted_sum / s.total_population;
              
              // Determine Dominant Party
              let maxScore = 0;
              let domParty = "Neutral";
              Object.entries(s.party_counts).forEach(([p, score]) => {
                  if (score > maxScore) { maxScore = score; domParty = p; }
              });
              s.dominant_party = domParty;
          }
      });

      return summary;
  }, [globalCalibratedVariants, hydratedVariants]);

  const genderStats = useMemo(() => {
    let m = 0, f = 0;
    Object.values(genderAgeSummary).forEach(g => { m += g.male_count || 0; f += g.female_count || 0; });
    const total = m + f || 1;
    return { malePct: Math.round((m/total)*100), femalePct: Math.round((f/total)*100) };
  }, [genderAgeSummary]);

  return (
    // FIX: Change main wrapper to h-screen overflow-y-auto to create a reliable scroll container for sticky elements
    <div className="h-screen w-full bg-slate-950 text-slate-200 font-sans selection:bg-sky-500/30 overflow-y-auto">
      
      <header className="px-6 py-5 border-b border-slate-700/50 bg-slate-900/95 backdrop-blur-md sticky top-0 z-50 shadow-lg">
        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-4 w-full md:w-auto">
                <div>
                    <h1 className="text-xl font-bold text-white tracking-tight leading-tight">Demographic Intelligence</h1>
                    <div className="flex items-center gap-3 text-xs text-slate-400 font-medium mt-0.5">
                        <span className="flex items-center gap-1.5"><Users size={12} className="text-slate-500"/> {Math.round(totalPop).toLocaleString()} Records</span>
                        <span className="w-1 h-1 rounded-full bg-slate-700"/>
                        <span className="flex items-center gap-1.5"><MapPin size={12} className="text-slate-500"/> {totalWards} Wards</span>
                        <span className="w-1 h-1 rounded-full bg-slate-700"/>
                        <span className="flex items-center gap-1.5"><Calendar size={12} className="text-slate-500"/> {new Date().toLocaleDateString('en-GB')}</span>
                    </div>
                </div>
            </div>
            
            <div className="flex items-center gap-6 w-full md:w-auto justify-end">
                <div className="hidden lg:flex flex-col items-end">
                    <span className="text-[10px] uppercase font-bold text-slate-500 tracking-wider mb-1.5">Gender Split</span>
                    <div className="flex items-center gap-3">
                        <span className="text-xs font-bold text-blue-400 font-mono">{genderStats.malePct}% M</span>
                        <div className="w-32 h-2 bg-slate-800 rounded-full overflow-hidden flex relative">
                            <div className="bg-blue-500 h-full" style={{width: `${genderStats.malePct}%`}}/>
                            <div className="bg-pink-500 h-full" style={{width: `${genderStats.femalePct}%`}}/>
                            <div className="absolute left-1/2 top-0 bottom-0 w-px bg-slate-900/50" />
                        </div>
                        <span className="text-xs font-bold text-pink-400 font-mono">{genderStats.femalePct}% F</span>
                    </div>
                </div>

                <div className="h-8 w-px bg-slate-800 mx-2 hidden lg:block"/>
                
                <div className="flex bg-slate-800/80 p-1 rounded-lg border border-slate-700/50 shadow-sm">
                    {['all', 'female', 'male'].map(g => (
                        <button 
                            key={g}
                            onClick={() => setActiveGender(g)} 
                            className={`px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider rounded-md transition-all flex items-center gap-2 ${activeGender === g ? (g==='female'?'bg-pink-600':g==='male'?'bg-blue-600':'bg-indigo-600') + ' text-white shadow-md transform scale-105' : 'text-slate-400 hover:text-slate-200 hover:bg-slate-700/50'}`}
                        >
                            {g === 'all' ? 'All' : g === 'female' ? 'Women' : 'Men'}
                        </button>
                    ))}
                </div>
            </div>
        </div>
      </header>

      <main className="p-8 space-y-8 pb-20">
        
        {/* SECTION 1: KPI HEADER */}
        <DemographicKPIHeader 
            totalPop={totalPop} 
            totalWards={totalWards} 
            genderStats={genderStats} 
            predictedVoteShares={predictedVoteShares} 
        />

        {/* SECTION 2: THE DEMOGRAPHIC HYDRAULIC (SANKEY) - FULL WIDTH */}
        <div className="grid grid-cols-1 xl:grid-cols-12 gap-8">
            <Panel title="The Demographic Hydraulic" icon={GitPullRequest} className="xl:col-span-12 min-h-[750px] flex flex-col">
               <div className="mb-4 flex items-center justify-between">
                     <p className="text-sm text-slate-400">
                        Visualizing the flow from <strong>Cohorts</strong> (Left) to <strong>Variants</strong> (Middle) to <strong>Predicted Vote</strong> (Right). 
                        <span className="block text-xs text-slate-500 mt-1">Flows are calibrated using variant-specific propensities and population data.</span>
                    </p>
                    <div className="flex items-center space-x-4 text-xs text-slate-500">
                        <span className="flex items-center"><div className="w-2 h-2 rounded-full bg-slate-500 mr-1"/> Cohort</span>
                        <span className="flex items-center"><div className="w-2 h-2 rounded-full bg-slate-300 mr-1"/> Variant</span>
                        <span className="flex items-center"><div className="w-2 h-2 rounded-full bg-blue-500 mr-1"/> Party Vote</span>
                    </div>
               </div>
               <div className="flex-grow flex items-center justify-center bg-slate-900/20 rounded-lg border border-slate-700/30 p-4">
                  <DemographicSankey 
                    variantData={filteredVariants} 
                    predictedVoteShares={predictedVoteShares}
                    totalPopulation={totalPop}
                  />
               </div>
            </Panel>
        </div>

        {/* SECTION 3: DENSITY & CONSTELLATION - MOVED UNDER SANKEY */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
           <Panel title="Campaign Density Matrix" icon={BarChart3} className="lg:col-span-7">
              <CampaignHeatmap genderAgeSummary={calibratedGenderAgeSummary} activeGender={activeGender} variants={globalCalibratedVariants} cohortLookup={COHORT_LOOKUP} />
           </Panel>
           
           <Panel title="Global Variant Constellation" icon={ScatterChart} className="lg:col-span-5 min-h-[500px]">
              <div className="mb-2 text-xs text-slate-400">
                  Physics simulation of all 84+ variants. <br/>
                  <span className="text-slate-500">X: Ideology (Left-Right) | Y: Turnout Probability | Glow: Population Density</span>
              </div>
              <GlobalStarChart variantData={globalCalibratedVariants} />
           </Panel>
        </div>

        {/* SECTION 4: MOBILISATION & FRACTURE */}
        <div className="grid grid-cols-1 xl:grid-cols-12 gap-8 mb-8">
            <Panel title="Mobilisation Matrix" icon={Megaphone} className="xl:col-span-7">
                <MobilisationMatrix filteredVariants={filteredVariants} />
            </Panel>
            <Panel title="Structural Fracture Points" icon={Scale} className="xl:col-span-5">
                <StructuralFracture />
            </Panel>
        </div>

        {/* SECTION 5: WAR ROOM */}
        <div className="grid grid-cols-1 xl:grid-cols-12 gap-8">
            <Panel title="Future-Cast War Room" icon={Eye} className="xl:col-span-12">
               <ScenarioWarRoom />
            </Panel>
        </div>

        {/* SECTION 6: COHORT DEEP DIVE */}
        <section className="space-y-6">
          <div className="flex items-center gap-2">
             <Filter className="text-sky-400" size={18} />
             <h3 className="text-xs font-bold text-slate-500 uppercase tracking-widest">Cohort Deep-Dive Analysis</h3>
          </div>
          
          <div className="flex gap-4 overflow-x-auto pb-4 pt-2 px-1 scrollbar-thin scrollbar-thumb-slate-700 scrollbar-track-transparent">
            {AGE_BUCKETS.map((ageKey) => {
               const bucketVariants = filteredVariants.filter(v => {
                  if(ageKey === v.age_range) return true;
                  const avgAge = v.avg_age || 40;
                  if(ageKey === '18-24') return avgAge < 25;
                  if(ageKey === '25-34') return avgAge >= 25 && avgAge < 35;
                  if(ageKey === '35-44') return avgAge >= 35 && avgAge < 45;
                  if(ageKey === '45-54') return avgAge >= 45 && avgAge < 55;
                  if(ageKey === '55-64') return avgAge >= 55 && avgAge < 65;
                  if(ageKey === '64-75') return avgAge >= 65 && avgAge < 76;
                  if(ageKey === '75+')   return avgAge >= 76;
                  return false;
               });
               const bucketPop = bucketVariants.reduce((a,b)=>a+(b.total_population||0),0);
               const staticData = COHORT_LOOKUP[ageKey] || {};

               return (
                   <CohortSelectorCard 
                        key={ageKey}
                        ageKey={ageKey}
                        data={staticData}
                        totalPop={bucketPop}
                        grandTotalPop={totalPop}
                        isActive={selectedCohort === ageKey}
                        onClick={() => setSelectedCohort(ageKey)}
                   />
               )
            })}
          </div>

          <div className="bg-slate-900/30 border border-slate-700/50 rounded-2xl p-6">
             <AnimatePresence mode="wait">
                {selectedCohort && (
                    <motion.div
                        key={selectedCohort}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.2 }}
                    >
                        {(() => {
                            const bucketVariants = filteredVariants.filter(v => {
                                if(selectedCohort === v.age_range) return true;
                                const avgAge = v.avg_age || 40;
                                if(selectedCohort === '18-24') return avgAge < 25;
                                if(selectedCohort === '25-34') return avgAge >= 25 && avgAge < 35;
                                if(selectedCohort === '35-44') return avgAge >= 35 && avgAge < 45;
                                if(selectedCohort === '45-54') return avgAge >= 45 && avgAge < 55;
                                if(selectedCohort === '55-64') return avgAge >= 55 && avgAge < 65;
                                if(selectedCohort === '64-75') return avgAge >= 65 && avgAge < 76;
                                if(selectedCohort === '75+')   return avgAge >= 76;
                                return false;
                            });
                            const staticData = COHORT_LOOKUP[selectedCohort] || {};

                            return (
                                <CohortDetailView 
                                    ageKey={selectedCohort}
                                    staticData={staticData}
                                    variantData={bucketVariants}
                                    voteShares={predictedVoteShares}
                                    totalPopulation={totalPop}
                                    activeGender={activeGender}
                                />
                            )
                        })()}
                    </motion.div>
                )}
             </AnimatePresence>
          </div>

        </section>
      </main>
    </div>
  );
}